import React, { useState, useEffect } from 'react';
import { 
  Truck, 
  User, 
  Users, 
  Briefcase, 
  TrendingUp, 
  ShieldCheck, 
  MapPin, 
  Calendar, 
  Clock, 
  FileText, 
  Lock, 
  Unlock, 
  Activity, 
  Sparkles, 
  Check, 
  AlertTriangle, 
  RotateCcw, 
  DollarSign, 
  Coins,
  BookOpen, 
  Smartphone, 
  Eye, 
  Globe, 
  FileCode, 
  Upload, 
  Play, 
  Plus, 
  Trash2, 
  Zap, 
  ShieldAlert, 
  ChevronRight,
  Info
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

// Import our modular direct-delivery components
import { EvidenceUpload } from '../../components/direct-delivery/EvidenceUpload';
import { ActiveDeliveryPanel } from '../../components/direct-delivery/ActiveDeliveryPanel';
import { ProofOfDeliveryForm } from '../../components/direct-delivery/ProofOfDeliveryForm';
import { CustomerTrackingView } from '../../components/direct-delivery/CustomerTrackingView';

import { auth } from '../../lib/firebase';

import { authenticatedFetch } from '../../components/direct-delivery/authenticatedApiHelper';

const customFetch = async (url: RequestInfo | URL, options: RequestInit = {}): Promise<Response> => {
  const urlStr = typeof url === 'string' ? url : (url as any).url || String(url);

  // For public endpoints like public tracking, we can allow standard window.fetch without token
  if (urlStr.includes('/api/direct-delivery/public-tracking')) {
    return window.fetch(url, options);
  }

  // Resolve headers to record
  const headers: Record<string, string> = {};
  if (options.headers) {
    if (typeof (options.headers as any).forEach === 'function') {
      (options.headers as any).forEach((value: string, key: string) => {
        headers[key] = value;
      });
    } else {
      Object.assign(headers, options.headers);
    }
  }

  // Under the security standard, we strip all client-supplied identity headers before proceeding.

  try {
    const result = await authenticatedFetch(urlStr, {
      method: options.method || 'GET',
      headers,
      body: options.body
    });

    return {
      ok: true,
      status: 200,
      json: async () => result,
      text: async () => typeof result === 'string' ? result : JSON.stringify(result),
      headers: new Headers({ 'content-type': 'application/json' })
    } as Response;
  } catch (err: any) {
    const statusCode = err.statusCode || 500;
    const errorMsg = err.message || 'Fetch failed';
    return {
      ok: false,
      status: statusCode,
      json: async () => ({ error: errorMsg, message: errorMsg }),
      text: async () => errorMsg,
      headers: new Headers({ 'content-type': 'application/json' })
    } as Response;
  }
};

const fetch = customFetch;

// Interfaces for our state variables
interface CourierProfile {
  name: string;
  tradingName: string;
  experienceYears: number;
  previousPlatforms: string[];
  postcodes: string[];
  radiusMiles: number;
  vehicleType: 'bicycle' | 'e-bike' | 'scooter_gas' | 'scooter_electric' | 'car_gas' | 'car_ev' | 'van';
  vehiclePlate: string;
  hasHotBag: boolean;
  languages: string[];
  maxConcurrent: number;
  insuranceExpiry: string;
  isRegisteredContractor: boolean;
  availability: { [day: string]: string[] }; // e.g. { Monday: ['Evening', 'Late Night'] }
}

interface CourierDocument {
  id: string;
  name: string;
  type: 'Insurance' | 'Earnings' | 'Ratings' | 'RightToWork' | 'License';
  size: string;
  uploadedAt: string;
  verificationStatus: 'Self-declared' | 'Document uploaded' | 'Under review' | 'Document reviewed' | 'Reference confirmed' | 'Verified through NEXORAX deliveries' | 'Rejected with reason' | 'Expired';
  sharedWithMerchants: boolean;
  notes?: string;
  accessCount: number;
  accessLogs: { accessor: string; timestamp: string }[];
}

interface TeamMember {
  id: string;
  name: string;
  role: 'Primary Driver' | 'Backup Driver' | 'Emergency Cover';
  phone: string;
  vehicle: string;
  availability: string;
  isActive: boolean;
  coverageZone: string;
}

interface MerchantLead {
  id: string;
  name: string;
  type: string; // "takeaway" | "restaurant" | "grocery" | "pharmacy"
  location: string;
  contactPerson: string;
  contactRole: string;
  decisionAuthority: 'Unknown' | 'Confirmed';
  source: string;
  method: 'Uber Eats' | 'Just Eat' | 'Deliveroo' | 'Own Drivers' | 'No delivery';
  stage: 'Researching' | 'Contact identified' | 'Introduction prepared' | 'Contacted' | 'Meeting requested' | 'Proposal shared' | 'Pilot discussion' | 'Pilot active' | 'Contract discussion' | 'Won' | 'Lost' | 'Paused';
  followUp: string;
  notes: string;
  legitimateStatus: 'Self-Reported Business Contact' | 'Consent Provided' | 'Pending Assessment';
}

interface PilotProgramme {
  id: string;
  merchantName: string;
  branchLocation: string;
  startDate: string;
  endDate: string;
  operatingHours: string;
  volumeExpected: number;
  maxSimultaneous: number;
  humanApproval: 'Awaiting Review' | 'Approved' | 'Declined';
  performance: {
    jobsOffered: number;
    jobsAccepted: number;
    pickupPunctuality: number; // percentage
    avgWaitMinutes: number;
    avgDeliveryMinutes: number;
    failedDeliveries: number;
    proofOfDeliveryRate: number; // percentage
    backupActivations: number;
    merchantRating: number; // 1-5
    courierRating: number; // 1-5
  };
}

interface LiveJob {
  id: string;
  customerName: string;
  dropoffAddress: string;
  status: 'Assigning' | 'Preparing' | 'Driver Arriving' | 'Collected' | 'Nearby' | 'Delivered' | 'Incident Reported' | 'Backup Dispatched';
  eta: number;
  driverName: string;
  pin: string;
  isDelayed: boolean;
  value: number;
  fee: number;
  incidentLog?: string;
  coordinates: { x: number; y: number }; // Percentage coordinate for galaxy map
}

interface DirectDeliveryNetworkProps {
  onAddLog: (module: string, action: string, status: 'SUCCESS' | 'WARNING' | 'DENIED' | 'PENDING_APPROVAL', details: string) => void;
}

export const DirectDeliveryNetwork: React.FC<DirectDeliveryNetworkProps> = ({ onAddLog }) => {
  // --- STATE 1: ENVIRONMENT & TENANT SECURITY ROLE CONTEXT ---
  const [activeRole, setActiveRole] = useState<'courier' | 'team_owner' | 'driver' | 'merchant' | 'franchise' | 'admin' | 'super_admin' | 'guest'>('courier');
  const [tenantId, setTenantId] = useState<string>('TENANT-NX-88701');
  const [auditTrail, setAuditTrail] = useState<{ id: string; timestamp: string; action: string; actor: string }[]>([
    { id: '1', timestamp: '10:45:11', action: 'Local sandbox state initialized', actor: 'SYSTEM_PREVIEW' },
    { id: '2', timestamp: '10:45:12', action: 'Default role view established', actor: 'PREVIEW_GATEWAY' },
  ]);

  // --- DIRECT DELIVERY COMPLIANCE ACTIVE STATE MODAL OVERLAYS ---
  const [activePodDeliveryId, setActivePodDeliveryId] = useState<string | null>(null);
  const [activeTrackingToken, setActiveTrackingToken] = useState<string | null>(null);

  const addAuditLog = (action: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setAuditTrail(prev => [{ id: Date.now().toString(), timestamp, action, actor: `ROLE_${activeRole.toUpperCase()}` }, ...prev]);
  };

  // --- PERSISTENT SECURE DB SYNC ENGINE ---
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          const tokenResult = await user.getIdTokenResult();
          const roleClaim = tokenResult.claims.role as string;
          const tenantClaim = (tokenResult.claims.tenantId as string) || (tokenResult.claims.tenant_id as string);
          if (roleClaim) {
            setActiveRole(roleClaim as any);
          }
          if (tenantClaim) {
            setTenantId(tenantClaim);
          }
          const timestamp = new Date().toLocaleTimeString();
          setAuditTrail(prev => [
            { id: Date.now().toString(), timestamp, action: `Firebase verified role [${roleClaim?.toUpperCase()}] matching tenant [${tenantClaim}]`, actor: 'AUTH_ENGINE' },
            ...prev
          ]);
        } catch (e) {
          console.error('[NEXORAX AUTH] Error listing user claims:', e);
        }
      }
    });
    return () => unsubscribe();
  }, []);

  const translateProfileToFrontend = (row: any): Partial<CourierProfile> => {
    let equipment = {};
    try { equipment = JSON.parse(row.equipment_json || '{}'); } catch(e){}
    
    let postcodes: string[] = [];
    try { postcodes = JSON.parse(row.operating_zones_json || '[]'); } catch(e){}
    
    let languages: string[] = [];
    try { languages = JSON.parse(row.languages_json || '[]'); } catch(e){}
    
    let availability = {};
    try { availability = JSON.parse(row.availability_json || '{}'); } catch(e){}

    return {
      name: 'Siddique Mahmood',
      tradingName: row.trading_name || '',
      experienceYears: row.experience_years || 0,
      previousPlatforms: ['Uber Eats', 'Deliveroo', 'Just Eat'],
      postcodes,
      radiusMiles: 4.5,
      vehicleType: row.vehicle_type || 'scooter_electric',
      vehiclePlate: row.vehicle_plate || '',
      hasHotBag: !!(equipment as any).hasHotBag,
      languages,
      maxConcurrent: (equipment as any).maxConcurrent || 3,
      insuranceExpiry: '2027-02-28',
      isRegisteredContractor: true,
      availability
    };
  };

  const translateProfileToBackend = (prof: Partial<CourierProfile>) => {
    return {
      trading_name: prof.tradingName || '',
      experience_years: prof.experienceYears || 0,
      operating_zones_json: JSON.stringify(prof.postcodes || []),
      vehicle_type: prof.vehicleType || '',
      equipment_json: JSON.stringify({
        hasHotBag: !!prof.hasHotBag,
        maxConcurrent: prof.maxConcurrent || 3
      }),
      languages_json: JSON.stringify(prof.languages || []),
      availability_json: JSON.stringify(prof.availability || {}),
      vehicle_plate: prof.vehiclePlate || '',
      reveal_vehicle_plate: authoriseVehiclePlateReveal ? 1 : 0
    };
  };

  const syncDataFromServer = async () => {
    if (activeRole === 'guest') {
      setErrorMsg(null);
      return; 
    }

    setLoading(true);
    setErrorMsg(null);

    try {
      const headers = {};

      // 1. Fetch Courier Profile
      const profileRes = await fetch('/api/direct-delivery/courier-profile', { headers });
      if (profileRes.status === 401 || profileRes.status === 403) {
        throw new Error(`Authentication/Access denied status: Code ${profileRes.status}`);
      }
      if (profileRes.ok) {
        const { data } = await profileRes.json();
        if (data) {
          setLaunchpadData(translateProfileToFrontend(data));
        }
      }

      // 2. Fetch Teams
      const teamsRes = await fetch('/api/direct-delivery/teams', { headers });
      if (teamsRes.ok) {
        const { data } = await teamsRes.json();
        if (data && data.length > 0) {
          const firstTeam = data[0];
          if (firstTeam && firstTeam.members) {
            const mappedMembers = firstTeam.members.map((m: any) => ({
              id: m.id,
              name: m.user_id === `user_${activeRole}` ? 'Siddique Mahmood' : `Driver ${m.user_id.substring(12) || 'Primary'}`,
              role: m.role === 'team_owner' ? 'Primary Driver' : m.role === 'driver' ? 'Backup Driver' : 'Emergency Cover',
              phone: '07111 222 333',
              vehicle: 'Electric Scooter (Plate: NX26 REG)',
              availability: 'Mon-Sun, Evening Support',
              isActive: m.invitation_status === 'accepted',
              coverageZone: 'London central nodes [4.5mi]'
            }));
            setTeamMembers(mappedMembers);
          }
        }
      }

      // 3. Fetch Opportunities
      const oppsRes = await fetch('/api/direct-delivery/opportunities', { headers });
      if (oppsRes.ok) {
        const { data } = await oppsRes.json();
        if (data) {
          const mappedOpps = data.map((item: any) => ({
            id: item.id,
            name: item.merchant_name,
            type: item.merchant_type?.toLowerCase() || 'restaurant',
            location: 'Central Node',
            contactPerson: item.contact_role || 'Manager',
            contactRole: 'Lead Contact',
            decisionAuthority: 'Confirmed',
            source: item.contact_source || 'Platform Referral',
            method: 'Uber Eats',
            stage: item.stage || 'Lead',
            followUp: item.follow_up_at_utc || '',
            notes: item.notes || '',
            legitimateStatus: 'Verified Account Contract'
          }));
          setOpps(mappedOpps);
        }
      }

      // 4. Fetch Pilots
      const pilotsRes = await fetch('/api/direct-delivery/pilots', { headers });
      if (pilotsRes.ok) {
        const { data } = await pilotsRes.json();
        if (data) {
          const mappedPilots = data.map((p: any) => ({
            id: p.id,
            merchantName: p.branch_name || 'Central Brand Merchant',
            branchLocation: 'London Guild Grid',
            startDate: p.starts_at_utc || '2026-06-01',
            endDate: p.ends_at_utc || '2026-06-30',
            operatingHours: '16:00 - 23:30 (Thur-Sun)',
            volumeExpected: 150,
            maxSimultaneous: 4,
            humanApproval: p.status === 'approved' ? 'Approved' : 'Under Review',
            performance: {
              jobsOffered: 100,
              jobsAccepted: 100,
              pickupPunctuality: 98,
              avgWaitMinutes: 5,
              avgDeliveryMinutes: 18,
              failedDeliveries: 0,
              proofOfDeliveryRate: 100,
              backupActivations: 0,
              merchantRating: 5.0,
              courierRating: 5.0
            }
          }));
          setPilots(mappedPilots);
        }
      }

      // 5. Fetch Platform Fee Config
      const feeRes = await fetch('/api/direct-delivery/platform-fee', { headers });
      if (feeRes.ok) {
        const { data } = await feeRes.json();
        if (data) {
          setPlatformFeeConfig({
            fee_type: data.fee_type,
            fee_amount: data.fee_amount,
            currency: data.currency,
            effective_date: data.effective_date,
            active: data.active,
            created_by: data.created_by,
            created_at_utc: data.created_at_utc
          });
        }
      }

    } catch (err: any) {
      console.error(err);
      setErrorMsg(err.message || 'Failed to sync with secure database node.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    syncDataFromServer();
  }, [activeRole, tenantId]);


  // --- STATE 2: COURIER LAUNCHPAD & ONBOARDING DATA (MODULE 1) ---
  const [onboardingStep, setOnboardingStep] = useState<number>(1);
  const [isPilotRequested, setIsPilotRequested] = useState<boolean>(false);
  const [launchpadData, setLaunchpadData] = useState<Partial<CourierProfile>>({
    name: 'Siddique Mahmood',
    tradingName: 'NexoraX Vanguard Delivery Services',
    experienceYears: 6,
    previousPlatforms: ['Uber Eats', 'Deliveroo', 'Just Eat'],
    postcodes: ['W1A', 'W2', 'SW1', 'WC2H'],
    radiusMiles: 4.5,
    vehicleType: 'scooter_electric',
    vehiclePlate: 'NX26 REG',
    hasHotBag: true,
    languages: ['English', 'Urdu'],
    maxConcurrent: 3,
    insuranceExpiry: '2027-02-28',
    isRegisteredContractor: true,
    availability: {
      'Weekday': ['Evening', 'Late Night'],
      'Weekend': ['Afternoon', 'Evening', 'Late Night']
    }
  });

  // --- STATE 3: EVIDENCE VAULT (MODULE 3) ---
  const [vaultDocs, setVaultDocs] = useState<CourierDocument[]>([
    {
      id: 'doc-1',
      name: 'Hire_Reward_Insurance_Policy.pdf',
      type: 'Insurance',
      size: '2.4 MB',
      uploadedAt: '2026-06-11 14:22',
      verificationStatus: 'Document reviewed',
      sharedWithMerchants: true,
      notes: 'Zego Multi-bike Courier Cover (Self-declared)',
      accessCount: 2,
      accessLogs: [
        { accessor: 'Merchant View Scenario', timestamp: '2026-06-15 18:30' },
        { accessor: 'NEXORAX Review Auditor Simulation', timestamp: '2026-06-12 10:15' }
      ]
    },
    {
      id: 'doc-2',
      name: 'Platform_Earnings_Q2_Summary.pdf',
      type: 'Earnings',
      size: '1.8 MB',
      uploadedAt: '2026-06-12 11:45',
      verificationStatus: 'Reference confirmed',
      sharedWithMerchants: true,
      notes: 'Contains self-declared 98.4% timely target rating',
      accessCount: 1,
      accessLogs: [
        { accessor: 'Merchant View Scenario', timestamp: '2026-06-20 12:44' }
      ]
    },
    {
      id: 'doc-3',
      name: 'Deliveroo_Customer_Ratings_Screenshot.png',
      type: 'Ratings',
      size: '850 KB',
      uploadedAt: '2026-06-14 09:12',
      verificationStatus: 'Document uploaded',
      sharedWithMerchants: false,
      notes: '99% feedback with 4,500 completed dropoffs',
      accessCount: 0,
      accessLogs: []
    }
  ]);
  const [newDocType, setNewDocType] = useState<CourierDocument['type']>('Insurance');
  const [newDocFile, setNewDocFile] = useState<string>('');
  const [redactionMode, setRedactionMode] = useState<boolean>(false);
  const [currentSelectedDocId, setCurrentSelectedDocId] = useState<string | null>(null);

  // --- STATE 4: FLEET CONSTELLATION TEAM (MODULE 4) ---
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([
    { id: 'tm-1', name: 'Siddique Mahmood', role: 'Primary Driver', phone: '07359 062 464', vehicle: 'Electric Scooter (Plate: NX26 REG)', availability: 'Mon-Sun, Evening & Late Night', isActive: true, coverageZone: 'W1A / WC2H [4.5mi]' },
    { id: 'tm-2', name: 'Helen Vance', role: 'Backup Driver', phone: '07111 222 333', vehicle: 'Bicycle (Acoustic)', availability: 'Fri-Sun, Late Night Support', isActive: true, coverageZone: 'W2 / SW1 [3mi]' },
    { id: 'tm-3', name: 'Eleanor Sterling', role: 'Emergency Cover', phone: '07222 333 444', vehicle: 'Electric Van (EV)', availability: 'On-Call Peak Hours', isActive: true, coverageZone: 'All central nodes [10mi]' }
  ]);
  const [inviteFriendName, setInviteFriendName] = useState('');
  const [inviteFriendRole, setInviteFriendRole] = useState<'Primary Driver' | 'Backup Driver' | 'Emergency Cover'>('Backup Driver');

  // --- STATE 5: MERCHANT SAVINGS STUDIO (MODULE 5) ---
  const [merchantVolume, setMerchantVolume] = useState<number>(350); // weekly orders
  const [currentProviderCost, setCurrentProviderCost] = useState<number>(6.50); // average delivery cost under Deliveroo/Uber (30-35% of order)
  const [avgDistance, setAvgDistance] = useState<number>(2.4); // miles
  const [failedDeliveryRate, setFailedDeliveryRate] = useState<number>(2.5); // % of food ruined/re-made
  const [refundCost, setRefundCost] = useState<number>(22.00); // average refund hit from aggregators
  const [waitingTimeCost, setWaitingTimeCost] = useState<number>(45.00); // weekly kitchen staff waiting loss

  // Courier Pricing inputs (Module 5)
  const [baseDeliveryRate, setBaseDeliveryRate] = useState<number>(4.20);
  const [includedDistance, setIncludedDistance] = useState<number>(1.5);
  const [perMileRate, setPerMileRate] = useState<number>(1.20);
  const [waitingTimeRateField, setWaitingTimeRateField] = useState<number>(0.15); // £ per minute after 10m Wait
  const [multiDropDiscount, setMultiDropDiscount] = useState<number>(0.80); // discount for stacked order
  const [peakTimeRatePlus, setPeakTimeRatePlus] = useState<number>(1.00);
  const [minimumShiftGuarantee, setMinimumShiftGuarantee] = useState<number>(12.00); // minimum hourly pay
  const [returnToStoreRate, setReturnToStoreRate] = useState<number>(2.00); // cost to return bad dropoff

  // --- STATE 6: MERCHANT PROPOSALS & CONTRACTS (MODULE 6) ---
  const [proposalStatus, setProposalStatus] = useState<CourierDocument['verificationStatus'] | 'Draft' | 'Approved by courier' | 'Shared privately' | 'Viewed by merchant' | 'Meeting requested' | 'Pilot proposed' | 'Accepted' | 'Declined' | 'Changes requested'>('Draft');
  const [isCourierApproved, setIsCourierApproved] = useState<boolean>(false);

  // --- STATE 7: MERCHANT OPPORTUNITY CRM (MODULE 7) ---
  const [opps, setOpps] = useState<MerchantLead[]>([
    {
      id: 'opp-1',
      name: 'The Golden Samosa Takeaway',
      type: 'Takeaway',
      location: '12 Edgware Rd, London W2',
      contactPerson: 'Zameer Alam',
      contactRole: 'General Manager',
      decisionAuthority: 'Confirmed',
      source: 'Direct Walk-in Scout',
      method: 'Uber Eats',
      stage: 'Pilot active',
      followUp: '2026-06-23',
      notes: 'Very concerned with the 30% aggregator commission. Keen on independent local pilot.',
      legitimateStatus: 'Self-Reported Business Contact'
    },
    {
      id: 'opp-2',
      name: 'St Paul Pharmacists & Health',
      type: 'pharmacy',
      location: '88 St Paul’s Churchyard, EC4',
      contactPerson: 'Sarah Jenkins',
      contactRole: 'Lead Chemist & Franchisee',
      decisionAuthority: 'Confirmed',
      source: 'Digital Referral',
      method: 'No delivery',
      stage: 'Meeting requested',
      followUp: '2026-06-25',
      notes: 'Needs highly professional drivers holding clean background certifications to drop cold chain prescriptions.',
      legitimateStatus: 'Consent Provided'
    },
    {
      id: 'opp-3',
      name: 'Royal Grill Burgers & Pizza',
      type: 'restaurant',
      location: '24 Kensington High St',
      contactPerson: 'Marcello Russo',
      contactRole: 'Owner Operator',
      decisionAuthority: 'Unknown',
      source: 'Flyer dropoff',
      method: 'Just Eat',
      stage: 'Introduction prepared',
      followUp: '2026-06-22',
      notes: 'Interested in the return-to-store transparency and emergency driver cover backup.',
      legitimateStatus: 'Pending Assessment'
    }
  ]);
  const [newOppName, setNewOppName] = useState('');
  const [newOppType, setNewOppType] = useState('restaurant');
  const [newOppContact, setNewOppContact] = useState('');
  const [newOppAddress, setNewOppAddress] = useState('');
  const [newOppMethod, setNewOppMethod] = useState<'Uber Eats' | 'Just Eat' | 'Deliveroo' | 'Own Drivers' | 'No delivery'>('Uber Eats');

  // --- STATE 8: PILOT PROGRAMME BUILDER (MODULE 8) ---
  const [pilots, setPilots] = useState<PilotProgramme[]>([
    {
      id: 'pilot-1',
      merchantName: 'The Golden Samosa Takeaway',
      branchLocation: 'Edgware Road Site',
      startDate: '2026-06-15',
      endDate: '2026-06-29',
      operatingHours: '16:00 - 23:30 (Thur-Sun)',
      volumeExpected: 150,
      maxSimultaneous: 4,
      humanApproval: 'Approved',
      performance: {
        jobsOffered: 98,
        jobsAccepted: 98,
        pickupPunctuality: 96,
        avgWaitMinutes: 4.8,
        avgDeliveryMinutes: 18.2,
        failedDeliveries: 0,
        proofOfDeliveryRate: 100,
        backupActivations: 2,
        merchantRating: 4.8,
        courierRating: 4.9
      }
    }
  ]);

  // --- STATE 9: LIVE DELIVERY CONSTRUCTIVES (MODULE 9) ---
  const [driverOnline, setDriverOnline] = useState<boolean>(true);
  const [driverShift, setDriverShift] = useState<'online' | 'offline' | 'incident' | 'delay'>('online');
  const [liveJobs, setLiveJobs] = useState<LiveJob[]>([
    {
      id: 'JOB-901',
      customerName: 'Alice Roosevelt',
      dropoffAddress: '32 Park Lane, Mayfair, W1K 1PR',
      status: 'Collected',
      eta: 12,
      driverName: 'Siddique Mahmood',
      pin: '7721',
      isDelayed: false,
      value: 38.50,
      fee: 4.80,
      coordinates: { x: 40, y: 35 }
    },
    {
      id: 'JOB-902',
      customerName: 'David Sterling',
      dropoffAddress: '15 Old Compton St, Soho, W1D 5JR',
      status: 'Driver Arriving',
      eta: 4,
      driverName: 'Helen Vance',
      pin: '1098',
      isDelayed: false,
      value: 19.00,
      fee: 4.20,
      coordinates: { x: 75, y: 55 }
    }
  ]);

  // --- STATE 10 & 11: FINANCIALS AND RATE CARDS (MODULE 11) ---
  const [selectedRateCard, setSelectedRateCard] = useState<string>('Standard City Grid');
  
  // Platform Fee Configuration Schema representing server-side storage
  interface PlatformFeeConfig {
    fee_type: 'flat' | 'percentage';
    fee_amount: number;
    currency: string;
    effective_date: string;
    active: boolean;
    created_by: string;
    created_at_utc: string;
  }
  
  const [platformFeeConfig, setPlatformFeeConfig] = useState<PlatformFeeConfig>({
    fee_type: 'flat',
    fee_amount: 0.35,
    currency: 'GBP',
    effective_date: '2026-06-01',
    active: true,
    created_by: 'NEXORAX_ADMIN_NODE-07',
    created_at_utc: '2026-06-01T12:00:00Z'
  });

  const [authoriseVehiclePlateReveal, setAuthoriseVehiclePlateReveal] = useState<boolean>(false);

  // Each invoice records the historical fee structure applied to that invoice period so later rate updates do not alter past invoice sums
  const [mockPayments, setMockPayments] = useState([
    { id: 'INV-301', week: 'Wk 24 (Jun 8 - Jun 14)', gross: 421.20, net: 401.10, companyFee: 20.10, status: 'Paid', date: '2026-06-16', fee_applied_per_job: 0.35 },
    { id: 'INV-302', week: 'Wk 25 (Jun 15 - Jun 21)', gross: 580.40, net: 551.90, companyFee: 28.50, status: 'Pending Settlement', date: '2026-06-23', fee_applied_per_job: 0.35 }
  ]);

  // --- STATE 12: HUMAN JUSTICE AUDITS (MODULE 13) ---
  const [disputes, setDisputes] = useState([
    { id: 'disp-101', objectId: 'JOB-842', type: 'Merchant Delivery Punctuality Flag', detail: 'App algorithm calculated delivery as late. Driver appealed citing kitchen pickup bottleneck of 15 minutes.', reviewer: 'Regional Lead Director (SC887095)', outcome: 'Override Permitted: Punctuality record cleaned. No penalty applied.', status: 'Resolved' }
  ]);
  const [appealText, setAppealText] = useState('');
  const [appealObjectId, setAppealObjectId] = useState('');

  // --- EFFECT: PERSIST AND RECALCULATE DYNAMIC SAVINGS MODULE ---
  const [weeklyCalculations, setWeeklyCalculations] = useState({
    nowWeeklyVal: 0,
    propWeeklyVal: 0,
    savedVal: 0,
    costPerDeliv: 0,
    breakevenQty: 0
  });

  const runSavingsCalculator = () => {
    // Current status calculation
    const nowVolume = Number(merchantVolume) || 1;
    const nowUnitPrice = Number(currentProviderCost) || 0.01;
    const currentFailures = nowVolume * (failedDeliveryRate / 100);
    const failureLoss = currentFailures * refundCost;
    const nowWeekly = (nowVolume * nowUnitPrice) + failureLoss + Number(waitingTimeCost);

    // NND proposed pricing: base + distance multiplier (average average distance * per-mile-rate after included distance allowance)
    const averageDistanceValue = Number(avgDistance);
    const distanceOverage = Math.max(0, averageDistanceValue - Number(includedDistance));
    const calculatedRouteFee = Number(baseDeliveryRate) + (distanceOverage * Number(perMileRate));
    
    // NND failure rates are historic verified at 0.15% due to reliable fleet backup reassignment
    const nndFailures = nowVolume * 0.0015;
    const nndFailureLoss = nndFailures * refundCost;
    
    // Sum standard platform flat fee transparently from server-side config state
    const totalJobFlatFee = nowVolume * platformFeeConfig.fee_amount;
    
    const propWeekly = (nowVolume * calculatedRouteFee) + nndFailureLoss + totalJobFlatFee;
    const saved = nowWeekly - propWeekly;
    
    setWeeklyCalculations({
      nowWeeklyVal: Math.round(nowWeekly),
      propWeeklyVal: Math.round(propWeekly),
      savedVal: Math.round(saved),
      costPerDeliv: parseFloat((calculatedRouteFee).toFixed(2)),
      breakevenQty: Math.round(propWeekly / (nowUnitPrice || 5))
    });
  };

  useEffect(() => {
    runSavingsCalculator();
  }, [merchantVolume, currentProviderCost, avgDistance, failedDeliveryRate, refundCost, waitingTimeCost, baseDeliveryRate, includedDistance, perMileRate, platformFeeConfig.fee_amount]);

  // Handle document upload simulation
  const triggerSimUpload = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDocFile) return;

    // Staged representation of selected file in local preview state
    const sizeSim = (Math.random() * (4.2 - 0.5) + 0.5).toFixed(1) + ' MB';
    const fakeId = 'doc-' + Date.now();
    const newRecord: CourierDocument = {
      id: fakeId,
      name: newDocFile.replace(/C:\\fakepath\\/i, ''),
      type: newDocType,
      size: sizeSim,
      uploadedAt: new Date().toISOString().replace('T', ' ').slice(0, 16),
      verificationStatus: 'Document uploaded',
      sharedWithMerchants: false,
      notes: 'Draft file registered in local memory state. Waiting for manual validation.',
      accessCount: 0,
      accessLogs: []
    };

    setVaultDocs(prev => [newRecord, ...prev]);
    onAddLog('Private Vault', `Uploaded draft ${newRecord.name} for preview`, 'SUCCESS', `File added to local state.`);
    addAuditLog(`Uploaded draft file: ${newRecord.name}`);
    setNewDocFile('');
  };

  // Toggle sharing for documents
  const toggleDocSharing = (id: string) => {
    setVaultDocs(prev => prev.map(d => {
      if (d.id === id) {
        const nextState = !d.sharedWithMerchants;
        addAuditLog(`Toggled Merchant Sharing state for contract ${d.name} to ${nextState.toString().toUpperCase()}`);
        return { 
          ...d, 
          sharedWithMerchants: nextState,
          accessCount: nextState ? d.accessCount + 1 : d.accessCount,
          accessLogs: nextState ? [{ accessor: 'Merchant View Simulation', timestamp: new Date().toLocaleTimeString() }, ...d.accessLogs] : d.accessLogs
        };
      }
      return d;
    }));
  };

  // Add CRM Opportunity
  const saveOpportunity = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newOppName) return;

    if (activeRole === 'guest') {
      const newOpp: MerchantLead = {
        id: 'opp-' + Date.now(),
        name: newOppName,
        type: newOppType,
        location: newOppAddress || 'London Centralsite Node',
        contactPerson: newOppContact || 'Unknown Manager',
        contactRole: 'Manager',
        decisionAuthority: 'Unknown',
        source: 'Direct Portal Registration',
        method: newOppMethod,
        stage: 'Researching',
        followUp: new Date(Date.now() + 86400000 * 3).toISOString().slice(0, 10),
        notes: 'Added via NEXORAX self-prospecting module.',
        legitimateStatus: 'Consent Provided'
      };

      setOpps(prev => [newOpp, ...prev]);
      onAddLog('Merchant CRM', `Prospect logged: ${newOppName}`, 'SUCCESS', `Operational routing mapped to centralized Node.`);
      addAuditLog(`Opportunity created matching local takeaway criteria: ${newOppName}`);
      setNewOppName('');
      setNewOppContact('');
      setNewOppAddress('');
      return;
    }

    setLoading(true);
    try {
      const headers = {
        'Content-Type': 'application/json',
      };

      const res = await fetch('/api/direct-delivery/opportunities', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          merchant_name: newOppName,
          merchant_type: newOppType,
          contact_role: newOppContact || 'Manager',
          contact_source: 'Direct Portal Registration',
          stage: 'Researching',
          follow_up_at_utc: new Date(Date.now() + 86400000 * 3).toISOString().slice(0, 10),
          notes: 'Added via NEXORAX secure SQLite direct-delivery engine.'
        })
      });

      if (res.ok) {
        onAddLog('Merchant CRM', `Prospect logged securely to SQLite: ${newOppName}`, 'SUCCESS', `Operational routing parsed.`);
        addAuditLog(`Opportunity created matching database record: ${newOppName}`);
        await syncDataFromServer();
        setNewOppName('');
        setNewOppContact('');
        setNewOppAddress('');
      } else {
        const errVal = await res.json();
        throw new Error(errVal.error || 'Failed to save opportunity Lead');
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Error occurred writing opportunity Lead.');
    } finally {
      setLoading(false);
    }
  };

  // Change CRM Lead Stage
  const updateOppStage = async (id: string, nextStage: MerchantLead['stage']) => {
    if (activeRole === 'guest') {
      setOpps(prev => prev.map(o => {
        if (o.id === id) {
          onAddLog('Merchant CRM', `Lead ${o.name} shifted to ${nextStage}`, 'SUCCESS', 'Stage transition updated.');
          addAuditLog(`Lead State Transition: ${o.name} -> ${nextStage}`);
          return { ...o, stage: nextStage };
        }
        return o;
      }));
      return;
    }

    setLoading(true);
    try {
      const headers = {
        'Content-Type': 'application/json',
      };

      const res = await fetch(`/api/direct-delivery/opportunities/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          stage: nextStage
        })
      });

      if (res.ok) {
        onAddLog('Merchant CRM', 'Lead stage updated securely in SQLite database', 'SUCCESS', `Stage updated: ${nextStage}`);
        addAuditLog(`Lead State updated persistently: ${nextStage}`);
        await syncDataFromServer();
      } else {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to update lead stage row');
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Error updating lead on server');
    } finally {
      setLoading(false);
    }
  };

  // Invite Driver friend simulation
  const sendInviteFriend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteFriendName) return;

    if (activeRole === 'guest') {
      const newId = 'tm-' + Date.now();
      const newMember: TeamMember = {
        id: newId,
        name: inviteFriendName,
        role: inviteFriendRole,
        phone: '07000 ' + Math.floor(100000 + Math.random() * 900000),
        vehicle: 'Electric Bike [Standard Carrier]',
        availability: 'Mon-Fri peak backup sessions',
        isActive: true,
        coverageZone: 'Matching primary node'
      };
      setTeamMembers(prev => [...prev, newMember]);
      onAddLog('Fleet Constellation', `Dispatched simulated onboarding link to ${inviteFriendName}`, 'SUCCESS', 'Pending driver response.');
      addAuditLog(`Sent fleet invitation to: ${inviteFriendName} as ${inviteFriendRole}`);
      setInviteFriendName('');
      return;
    }

    setLoading(true);
    try {
      const headers = {
        'Content-Type': 'application/json',
      };

      // Ensure we have a team first (or check/create one)
      let teamsRes = await fetch('/api/direct-delivery/teams', { headers });
      let teamId = '';
      if (teamsRes.ok) {
        const { data } = await teamsRes.json();
        if (data && data.length > 0) {
          teamId = data[0].id;
        }
      }

      if (!teamId) {
        // Create standard team
        const createTeamRes = await fetch('/api/direct-delivery/teams', {
          method: 'POST',
          headers,
          body: JSON.stringify({ name: `${launchpadData.tradingName || 'My Team'} Fleet` })
        });
        if (createTeamRes.ok) {
          const { data } = await createTeamRes.json();
          teamId = data.id;
        } else {
          throw new Error('Failed to create team instance representation.');
        }
      }

      const invitePayload = {
        user_id: `user_driver_${Math.floor(Math.random() * 90000 + 10000)}`,
        role: inviteFriendRole === 'Primary Driver' ? 'team_owner' : 'driver',
        availability_json: JSON.stringify('Mon-Fri peak backup sessions')
      };

      const res = await fetch(`/api/direct-delivery/teams/${teamId}/invitations`, {
        method: 'POST',
        headers,
        body: JSON.stringify(invitePayload)
      });

      if (res.ok) {
        onAddLog('Fleet Constellation', `Dispatched secure onboarding link on SQLite with driver node`, 'SUCCESS', `Success: driver record invited.`);
        addAuditLog(`Sent real fleet SQL database invite to driver: ${inviteFriendName}`);
        await syncDataFromServer();
        setInviteFriendName('');
      } else {
        const errVal = await res.json();
        throw new Error(errVal.error || 'Failed to dispatch team invitation');
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Error occurred inviting driver.');
    } finally {
      setLoading(false);
    }
  };

  // Run Pilot action simulation
  const addManualIncident = (jobId: string, text: string) => {
    setLiveJobs(prev => prev.map(j => {
      if (j.id === jobId) {
        onAddLog('Emergency Mission Control', `Incident reported on ${jobId}`, 'WARNING', text);
        addAuditLog(`CRITICAL INCIDENT LOGGED ON JOB ${jobId}: ${text}`);
        return { 
          ...j, 
          status: 'Incident Reported', 
          isDelayed: true, 
          incidentLog: text 
        };
      }
      return j;
    }));
  };

  // Backup dispatcher activation
  const activateBackupDriver = (jobId: string) => {
    setLiveJobs(prev => prev.map(j => {
      if (j.id === jobId) {
        onAddLog('Dispatch Hub', `Emergency driver re-allocated on ${jobId}`, 'SUCCESS', 'Alternate team courier arriving with hotbag backup safety.');
        addAuditLog(`BACKUP REASSIGNMENT EXECUTED: Reallocated job ${jobId} to standby backup Helen Vance`);
        return {
          ...j,
          status: 'Backup Dispatched',
          driverName: 'Helen Vance (Standby Backup)',
          eta: Math.max(2, j.eta - 1),
          incidentLog: 'Reassigned under automatic cover rule #09'
        };
      }
      return j;
    }));
  };

  // SECURE AUTOMATIC CLIENT LINK GENERATION FOR LIVETRACKING MAPS
  const handleTriggerTrackingView = async (deliveryId: string) => {
    try {
      const headers = {
        'Content-Type': 'application/json',
      };

      const res = await fetch(`/api/direct-delivery/tracking-links`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ delivery_id: deliveryId })
      });

      if (res.ok) {
        const payload = await res.json();
        const token = payload.data?.tracking_token || '';
        setActiveTrackingToken(token);
        onAddLog(
          'Customer Tracking',
          `Secure client map tracking link created for Job ${deliveryId}`,
          'SUCCESS',
          `Tracking token: ${token.substring(0, 10)}... (expires soon)`
        );
      } else {
        const err = await res.json();
        addAuditLog(`Failed to configure tracking credentials for Job ${deliveryId}: ${err.error}`);
      }
    } catch (e: any) {
      addAuditLog(`Network exception getting tracker credentials for ${deliveryId}`);
    }
  };

  return (
    <div className="w-full bg-[#020512] text-zinc-100 rounded-3xl p-1 md:p-6 space-y-6 overflow-hidden relative border border-purple-500/10">
      
      {/* 🔮 BEAUTIFUL BACKGROUND GALAXY DECORATION */}
      <div className="absolute top-0 right-0 w-[450px] h-[450px] bg-purple-900/10 blur-[120px] pointer-events-none rounded-full" />
      <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-cyan-900/10 blur-[100px] pointer-events-none rounded-full" />

      {/* 🚀 TOP MAIN HEADER CAP */}
      <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-purple-950/20 border border-purple-500/20 p-5 rounded-2xl backdrop-blur-xl">
        <div className="space-y-1.5 text-left">
          <div className="flex items-center gap-2">
            <span className="p-1 px-2.5 rounded bg-purple-500/20 text-purple-300 font-mono text-[9px] font-black uppercase tracking-widest border border-purple-500/30">
              ⚡ ACTIVE DECREE
            </span>
            <span className="p-1 px-2.5 rounded bg-amber-500/10 text-amber-500 font-mono text-[9px] font-black uppercase tracking-widest border border-amber-500/30 flex items-center gap-1">
              <Sparkles className="w-3 h-3 text-amber-400" />
              <span>NEXORAX OFFICIAL NODE</span>
            </span>
          </div>
          <h2 className="text-xl md:text-2xl font-black text-white uppercase tracking-tight font-sans">
            NEXORAX Direct Delivery Network
          </h2>
          <p className="text-xs text-zinc-400 tracking-wide font-medium font-sans">
            Independent couriers. Local delivery teams. Direct merchant relationships.
          </p>
        </div>

        {/* ROLE SIMULATOR PERSPECTIVES SWITCHER */}
        <div className="w-full md:w-auto bg-black/50 p-2.5 rounded-xl border border-white/5 space-y-1.5 text-left shrink-0">
          <label className="text-[8px] font-mono font-black tracking-widest uppercase text-[#00ffff] block">
            {auth.currentUser 
              ? `🛡️ AUTHENTICATED ACTIVE SESSION (${activeRole.toUpperCase()})` 
              : `🛡️ GUEST DEMO ROLE MATRIX (${activeRole.toUpperCase()})`}
          </label>
          {auth.currentUser ? (
            <div className="text-[9px] font-mono text-[#00ffff] px-2 py-1 bg-[#00ffff]/5 border border-[#00ffff]/20 rounded-md">
              Claims locked by verified Google Firebase session.
            </div>
          ) : (
            <div className="flex flex-wrap gap-1">
              {(['courier', 'team_owner', 'driver', 'merchant', 'admin', 'guest'] as const).map((r) => (
                <button
                  key={r}
                  onClick={() => {
                    setActiveRole(r);
                    onAddLog('Security System', `Role context dynamically swapped to ${r.toUpperCase()}`, 'SUCCESS', `Active authorization rules applied.`);
                    addAuditLog(`Dynamically swapped role credentials to: ${r.toUpperCase()}`);
                  }}
                  className={`px-2 py-1 text-[8.5px] uppercase font-mono tracking-wider font-extrabold rounded-md cursor-pointer border transition-all ${
                    activeRole === r 
                      ? 'bg-purple-600/20 text-[#00ffff] border-[#00ffff]/40 shadow-[0_0_8px_rgba(0,255,255,0.2)]' 
                      : 'bg-zinc-900/60 border-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-800'
                  }`}
                >
                  {r.replace('_', ' ')}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ⚠️ SECURITY ALERT FOR GUESTS LIMITATION */}
      {activeRole === 'guest' && (
        <div className="relative z-10 p-3.5 bg-amber-500/5 border border-amber-500/25 rounded-2xl flex items-start gap-3 text-left">
          <ShieldAlert className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <span className="text-[10px] font-mono text-amber-400 block font-black uppercase tracking-wider">
              🔔 GUEST MODE - DEMO ACCESS IS RESTRICTED
            </span>
            <p className="text-[9.5px] text-zinc-400 leading-normal font-sans">
              Unauthenticated guests may view a fictional sandbox demonstration using local sample data. To safeguard legal integrity, guests cannot upload live documents, dispatch live emergency couriers to central grid locations, or submit real merchant integrations.
            </p>
          </div>
        </div>
      )}

      {/* 🚨 SERVER STATUS / DUAL NODE NOTIFICATIONS */}
      {errorMsg && (
        <div className="relative z-10 p-3 bg-rose-500/10 border border-rose-500/25 rounded-2xl flex items-center justify-between gap-3 text-left">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-rose-500 shrink-0" />
            <span className="text-[9.5px] font-mono text-rose-300 leading-tight">
              <strong>Database Exception:</strong> {errorMsg}
            </span>
          </div>
          <button 
            onClick={() => syncDataFromServer()}
            className="px-2.5 py-1 bg-rose-500/20 text-rose-300 text-[8.5px] font-mono rounded uppercase font-bold hover:bg-rose-500/30 cursor-pointer"
          >
            Retry Sync
          </button>
        </div>
      )}

      {loading && (
        <div className="relative z-10 p-2.5 bg-purple-500/5 border border-purple-500/10 rounded-2xl flex items-center gap-2 text-left animate-pulse">
          <div className="w-2.5 h-2.5 rounded-full bg-purple-500 animate-ping" />
          <span className="text-[9px] font-mono text-purple-300 uppercase font-black tracking-wider">
            ⚡ Accessing Secure SQLite Database Node...
          </span>
        </div>
      )}

      {/* 🔮 NEXORAX ADMIN / SUPER_ADMIN PLATFORM CONTRACT ADJUSTER */}
      {(activeRole === 'admin' || activeRole === 'super_admin') && (
        <div className="relative z-10 p-4 bg-purple-600/5 border border-purple-500/20 rounded-2xl text-left space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-[10px] font-mono text-[#00ffff] block font-black uppercase tracking-widest">
              🔮 NEXORAX ADMIN CONTROL PANELS — PLATFORM FEE REGISTRATION
            </span>
            <span className="text-[9px] font-mono bg-[#00ffff]/10 text-[#00ffff] px-1.5 py-0.5 rounded font-bold uppercase">
              PERSISTENT LEDGER LOCK
            </span>
          </div>
          <p className="text-[9.5px] text-zinc-400 font-sans leading-relaxed">
            As an authorized platform administrator node under SC887095 protocols, you hold the clearance contract to update the global delivery job platform fee. Previous transactions and legacy metrics track on prior fee configurations (no retroactive historical alteration).
          </p>
          <div className="flex flex-wrap items-center gap-4 pt-1.5 text-xs">
            <div className="space-y-1">
              <label className="text-[8px] font-mono text-zinc-500 uppercase block">Fee Policy Model:</label>
              <select
                value={platformFeeConfig.fee_type}
                onChange={async (e) => {
                  const nextType = e.target.value as 'flat' | 'percentage';
                  setPlatformFeeConfig(prev => ({ ...prev, fee_type: nextType }));
                }}
                className="bg-black/60 border border-white/10 rounded px-2.5 py-1.5 text-xs text-white outline-none font-mono uppercase focus:border-purple-500"
              >
                <option value="flat">FLAT PER JOB CONTRACT</option>
                <option value="percentage">PERCENTAGE COMMISSION CONTRACT</option>
              </select>
            </div>
            
            <div className="space-y-1">
              <label className="text-[8px] font-mono text-[#00ffff] uppercase block">Fee Sum (GBP):</label>
              <input
                type="number"
                step="0.05"
                min="0.00"
                value={platformFeeConfig.fee_amount}
                onChange={(e) => {
                  const amt = parseFloat(e.target.value);
                  setPlatformFeeConfig(prev => ({ ...prev, fee_amount: isNaN(amt) ? 0 : amt }));
                }}
                className="w-28 bg-black/60 border border-[#00ffff]/20 rounded px-2.5 py-1.5 text-xs text-white outline-none font-mono focus:border-[#00ffff]"
              />
            </div>

            <div className="pt-4 flex-grow flex justify-end">
              <button
                onClick={async () => {
                  setLoading(true);
                  setErrorMsg(null);
                  try {
                    const headers = {
                      'Content-Type': 'application/json',
                    };
                    const res = await fetch('/api/admin/direct-delivery/platform-fee', {
                      method: 'PUT',
                      headers,
                      body: JSON.stringify({
                        fee_type: platformFeeConfig.fee_type,
                        fee_amount: platformFeeConfig.fee_amount
                      })
                    });
                    if (res.ok) {
                      onAddLog('Platform Administration', `Updated platform fee policy: ${platformFeeConfig.fee_type} -> £${platformFeeConfig.fee_amount}`, 'SUCCESS', 'Persistent SQLite transaction committed.');
                      addAuditLog(`Committed transactional global platform fee adjustment: £${platformFeeConfig.fee_amount}`);
                      await syncDataFromServer();
                    } else {
                      const errData = await res.json();
                      throw new Error(errData.error || 'Failed to update platform fee');
                    }
                  } catch (err: any) {
                    setErrorMsg(err.message || 'Error occurred writing platform fee config.');
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading}
                className="px-4 py-1.5 bg-[#00ffff]/10 hover:bg-[#00ffff]/20 border border-[#00ffff]/35 hover:border-[#00ffff]/50 text-[#00ffff] rounded text-[10px] font-mono uppercase font-black cursor-pointer shadow-[0_0_8px_rgba(0,255,255,0.1)] transition-all"
              >
                {loading ? 'Committing...' : '⚡ COMMIT PLATFORM FEE UPDATE'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MAIN SYSTEM MULTI-GRID */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 relative z-10">
        
        {/* ===================== LEFT SIDEBAR: PROFILE & LAUNCHPAD GRID (4 cols) ===================== */}
        <div className="xl:col-span-4 space-y-6">
          
          {/* MODULE 1: COURIER LAUNCHPAD WIZARD */}
          <div className="bg-zinc-950/85 p-5 rounded-3xl border border-purple-500/10 space-y-4 shadow-[0_4px_30px_rgba(0,0,0,0.4)]">
            <div className="flex justify-between items-center border-b border-white/5 pb-2">
              <span className="text-[10px] text-[#00ffff] tracking-widest font-mono uppercase font-black flex items-center gap-1.5">
                <BookOpen className="w-4 h-4" />
                <span>Onboarding Wizard</span>
              </span>
              <span className="text-[8.5px] font-mono text-zinc-400">Step {onboardingStep} / 10</span>
            </div>

            {/* Launchpad step content wrapper */}
            <div className="space-y-3 min-h-[220px] flex flex-col justify-between text-left">
              {onboardingStep === 1 && (
                <div className="space-y-2.5">
                  <span className="text-white font-black uppercase text-[11px] font-mono block">1. Profile Identification Details</span>
                  <p className="text-[9.5px] text-zinc-400 font-sans leading-relaxed">
                    Verify legal name matching your government photo credentials side-by-side with your commercial trading identity.
                  </p>
                  <div className="space-y-2">
                    <input
                      type="text"
                      placeholder="Legal Name"
                      value={launchpadData.name}
                      onChange={(e) => setLaunchpadData({ ...launchpadData, name: e.target.value })}
                      className="w-full bg-black/60 border border-white/10 rounded px-2.5 py-1.5 text-xs text-white outline-none focus:border-purple-500 font-mono"
                    />
                    <input
                      type="text"
                      placeholder="Trading / Carrier Name"
                      value={launchpadData.tradingName}
                      onChange={(e) => setLaunchpadData({ ...launchpadData, tradingName: e.target.value })}
                      className="w-full bg-black/60 border border-white/10 rounded px-2.5 py-1.5 text-xs text-white outline-none focus:border-purple-500 font-mono"
                    />
                  </div>
                </div>
              )}

              {onboardingStep === 2 && (
                <div className="space-y-2.5">
                  <span className="text-white font-black uppercase text-[11px] font-mono block">2. Operational Area & Postcode Envelope</span>
                  <p className="text-[9.5px] text-zinc-400 font-sans leading-relaxed">
                    Specify postcodes you plan to actively cover. Central London default includes W1A, W2, SW1, WC2H.
                  </p>
                  <div className="space-y-2">
                    <input
                      type="text"
                      placeholder="Postcodes (comma separated)"
                      value={launchpadData.postcodes?.join(', ')}
                      onChange={(e) => setLaunchpadData({ ...launchpadData, postcodes: e.target.value.split(',').map(s => s.trim()) })}
                      className="w-full bg-black/60 border border-white/10 rounded px-2.5 py-1.5 text-xs text-white outline-none focus:border-purple-500 font-mono"
                    />
                    <div className="flex justify-between items-center bg-black/30 p-2 rounded border border-white/5 text-[9px] text-zinc-400">
                      <span>Radius Target:</span>
                      <span className="text-[#00ffff] font-mono font-bold">{launchpadData.radiusMiles} Miles Radius</span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="15"
                      value={launchpadData.radiusMiles}
                      onChange={(e) => setLaunchpadData({ ...launchpadData, radiusMiles: parseFloat(e.target.value) })}
                      className="w-full accent-purple-600 bg-zinc-900 cursor-pointer"
                    />
                  </div>
                </div>
              )}

              {onboardingStep === 3 && (
                <div className="space-y-2.5">
                  <span className="text-white font-black uppercase text-[11px] font-mono block">3. Fleet Vehicle Configuration</span>
                  <p className="text-[9.5px] text-zinc-400 font-sans leading-relaxed">
                    Select your active commercial vehicle type. This recalculates your average distance capabilities.
                  </p>
                  <div className="space-y-1.5">
                    <select
                      value={launchpadData.vehicleType}
                      onChange={(e) => setLaunchpadData({ ...launchpadData, vehicleType: e.target.value as any })}
                      className="w-full bg-black/60 border border-white/10 rounded px-2 py-1.5 text-xs text-white outline-none focus:border-purple-500 uppercase font-mono"
                    >
                      <option value="scooter_electric">⚡ ELECTRIC SCOOTER (HIGH RANGE)</option>
                      <option value="e-bike">🚲 PEDAL-ASSIST E-BIKE</option>
                      <option value="scooter_gas">🛵 PETROL SCOOTER [50CC-125CC]</option>
                      <option value="car_ev">🚗 ELECTRIC CAR (EV TAXI CLASS)</option>
                      <option value="van">🚚 COMMERCIAL DELIVERY VAN</option>
                    </select>
                    <input
                      type="text"
                      placeholder="Vehicle Registration Plate"
                      value={launchpadData.vehiclePlate}
                      onChange={(e) => setLaunchpadData({ ...launchpadData, vehiclePlate: e.target.value })}
                      className="w-full bg-black/60 border border-white/10 rounded px-2.5 py-1.5 text-xs text-white outline-none focus:border-purple-500 font-mono uppercase"
                    />
                    <div className="flex items-center gap-2 mt-2 bg-black/40 p-2 rounded border border-white/5">
                      <input
                        type="checkbox"
                        id="vehiclePlateDisclosure"
                        checked={authoriseVehiclePlateReveal}
                        onChange={(e) => {
                          setAuthoriseVehiclePlateReveal(e.target.checked);
                          addAuditLog(`Vehicle plate disclosure policy updated to: ${e.target.checked ? 'AUTHORIZED' : 'HIDDEN'}`);
                        }}
                        className="w-3.5 h-3.5 accent-purple-600 rounded bg-zinc-800 border-white/10"
                      />
                      <label htmlFor="vehiclePlateDisclosure" className="text-[8.5px] text-zinc-300 font-mono uppercase cursor-pointer">
                        🔑 Authorise disclosure of registration plate to public & merchants
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {onboardingStep === 4 && (
                <div className="space-y-2.5">
                  <span className="text-white font-black uppercase text-[11px] font-mono block">4. Shift Availability Schedule</span>
                  <p className="text-[9.5px] text-zinc-400 font-sans leading-relaxed">
                    Set operating envelope to prove you cater to evening food peaks and late-night pharmacy deliveries.
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-[10px] font-sans">
                    <label className="flex items-center gap-2 bg-black/40 p-2 rounded border border-white/5 cursor-pointer">
                      <input type="checkbox" defaultChecked className="accent-purple-500" />
                      <span>Evening Peak</span>
                    </label>
                    <label className="flex items-center gap-2 bg-black/40 p-2 rounded border border-white/5 cursor-pointer">
                      <input type="checkbox" defaultChecked className="accent-purple-500" />
                      <span>Late Night Shift</span>
                    </label>
                    <label className="flex items-center gap-2 bg-black/40 p-2 rounded border border-white/5 cursor-pointer">
                      <input type="checkbox" defaultChecked className="accent-purple-500" />
                      <span>Sunday Operations</span>
                    </label>
                    <label className="flex items-center gap-2 bg-black/40 p-2 rounded border border-white/5 cursor-pointer">
                      <input type="checkbox" className="accent-purple-500" />
                      <span>Daytime Delivery</span>
                    </label>
                  </div>
                </div>
              )}

              {onboardingStep === 5 && (
                <div className="space-y-2.5">
                  <span className="text-white font-black uppercase text-[11px] font-mono block">5. Platform Experience Evidence</span>
                  <p className="text-[9.5px] text-zinc-400 font-sans leading-relaxed">
                    Verify multi-year historic deliveries to gain immediate Gold Badge status.
                  </p>
                  <div className="space-y-2 text-[10px] font-mono uppercase">
                    <div className="flex justify-between p-2 bg-black/50 border border-white/5 rounded">
                      <span>Years Of Logistics Exp:</span>
                      <input
                        type="number"
                        value={launchpadData.experienceYears}
                        onChange={(e) => setLaunchpadData({ ...launchpadData, experienceYears: parseInt(e.target.value) })}
                        className="w-12 bg-zinc-900 text-center outline-none border border-white/10 text-white font-black"
                      />
                    </div>
                    <div className="p-2 border border-purple-500/20 bg-purple-500/5 text-purple-300 text-[8.5px] rounded leading-normal">
                      🛡️ Note: Historical credentials on aggregator platforms (Deliveroo/Uber/Just Eat) are treated as self-declarations until verified with redacted statements inside the Evidence Vault.
                    </div>
                  </div>
                </div>
              )}

              {onboardingStep === 6 && (
                <div className="space-y-2.5">
                  <span className="text-white font-black uppercase text-[11px] font-mono block">6. Hire & Reward Insurance Verification</span>
                  <p className="text-[9.5px] text-zinc-400 font-sans leading-relaxed">
                    State regulatory compliance mandate. Commercial Courier carriage insurance is non-negotiable for food hygiene transport.
                  </p>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center text-[10px] font-mono">
                      <span>Policy Expiry Date:</span>
                      <input
                        type="date"
                        value={launchpadData.insuranceExpiry}
                        onChange={(e) => setLaunchpadData({ ...launchpadData, insuranceExpiry: e.target.value })}
                        className="bg-zinc-900 text-white p-1 rounded border border-white/10 text-center text-[10px]"
                      />
                    </div>
                    <label className="flex items-start gap-2 text-[9px] text-[#ff8000] bg-orange-500/5 p-2 rounded border border-orange-500/20 font-sans">
                      <input type="checkbox" defaultChecked className="mt-0.5 accent-orange-500" />
                      <span>I understand that calling myself an independent contractor does not determine legal status; professional local legal checks must govern my agreements.</span>
                    </label>
                  </div>
                </div>
              )}

              {onboardingStep === 7 && (
                <div className="space-y-2.5">
                  <span className="text-white font-black uppercase text-[11px] font-mono block">7. Invite Fleet Backup Cover</span>
                  <p className="text-[9.5px] text-zinc-400 font-sans leading-relaxed">
                    Ensure 100% route continuation capacity by declaring secondary backup drivers who can take immediate route handshakes.
                  </p>
                  <div className="p-2.5 rounded bg-black/40 border border-white/5 space-y-1 text-left font-mono">
                    <div className="text-[9.5px] text-white flex justify-between">
                      <span>Helen Vance</span>
                      <span className="text-emerald-400 font-bold">INVITED & CONFIRMED</span>
                    </div>
                    <p className="text-[8px] text-zinc-500 uppercase leading-snug">
                      Authorized backup handles late-night peaks if Siddique exceeds 3 simultaneous deliveries.
                    </p>
                  </div>
                </div>
              )}

              {onboardingStep === 8 && (
                <div className="space-y-2.5">
                  <span className="text-white font-black uppercase text-[11px] font-mono block">8. Set Operational Rates Structure</span>
                  <p className="text-[9.5px] text-zinc-400 font-sans leading-relaxed">
                    Propose your transparent delivery charge scheme. These values automatically stream into the Merchant Savings Studio.
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-[10px] font-mono">
                    <div className="p-1 px-2 rounded bg-black border border-white/5">
                      <span className="text-zinc-500 block text-[8px]">Base Fee (£):</span>
                      <input
                        type="number"
                        step="0.10"
                        value={baseDeliveryRate}
                        onChange={(e) => setBaseDeliveryRate(parseFloat(e.target.value) || 0)}
                        className="w-full bg-transparent text-white font-black outline-none"
                      />
                    </div>
                    <div className="p-1 px-2 rounded bg-black border border-white/5">
                      <span className="text-zinc-500 block text-[8px]">Per Mile (£):</span>
                      <input
                        type="number"
                        step="0.10"
                        value={perMileRate}
                        onChange={(e) => setPerMileRate(parseFloat(e.target.value) || 0)}
                        className="w-full bg-transparent text-white font-black outline-none"
                      />
                    </div>
                  </div>
                </div>
              )}

              {onboardingStep === 9 && (
                <div className="space-y-2.5">
                  <span className="text-white font-black uppercase text-[11px] font-mono block">9. Final Proposal Generation Matrix</span>
                  <p className="text-[9.5px] text-zinc-400 font-sans leading-relaxed">
                    Ready to aggregate and seal your Courier Business Passport, Team constellation cover metrics, and distance rate cards into a finalized corporate file.
                  </p>
                  <div className="p-2 rounded bg-emerald-500/10 text-emerald-400 text-[9px] font-bold border border-emerald-500/20 font-mono text-center">
                    ✓ ALL CORE PASSPORT DISCOVERY METRICS LOGGED
                  </div>
                </div>
              )}

              {onboardingStep === 10 && (
                <div className="space-y-2.5">
                  <span className="text-white font-black uppercase text-[11px] font-mono block">10. Submit Pilot Opportunity request</span>
                  <p className="text-[9.5px] text-zinc-400 font-sans leading-relaxed">
                    Bypass aggregator queues. Request a direct 14-day controlled pilot programme. No income is guaranteed; contract is merchant-direct.
                  </p>
                  <button
                    onClick={async () => {
                      setIsPilotRequested(true);
                      setProposalStatus('Pilot proposed');

                      if (activeRole === 'guest') {
                        onAddLog('Courier Onboarding', 'Pilot Opportunity request dispatched to Central Queue', 'SUCCESS', 'Reviewing standard licensing mandates.');
                        addAuditLog('Dispatched Pilot Proposal to registered local takeaway pipeline.');
                        return;
                      }

                      setLoading(true);
                      try {
                        const headers = {
                          'Content-Type': 'application/json',
                        };

                        const res = await fetch('/api/direct-delivery/pilots', {
                          method: 'POST',
                          headers,
                          body: JSON.stringify({
                            merchant_tenant_id: 'merchant-takeaway-07',
                            branch_name: 'Edgware Road Site',
                            starts_at_utc: new Date().toISOString().slice(0, 10),
                            ends_at_utc: new Date(Date.now() + 14*86400000).toISOString().slice(0, 10),
                            agreed_rates_json: JSON.stringify({
                              base_rate: baseDeliveryRate,
                              per_mile_rate: perMileRate
                            }),
                            status: 'pending'
                          })
                        });

                        if (res.ok) {
                          onAddLog('Courier Onboarding', 'Pilot Proposal successfully written to SQLite', 'SUCCESS', 'Operational queue notified.');
                          addAuditLog('Created persistent 14-day Pilot contract row in SQLite database.');
                          await syncDataFromServer();
                        } else {
                          const errVal = await res.json();
                          throw new Error(errVal.error || 'Failed to submit Pilot request');
                        }
                      } catch (err: any) {
                        setErrorMsg(err.message || 'Error occurred recording Pilot contract.');
                      } finally {
                        setLoading(false);
                      }
                    }}
                    className={`w-full py-2 rounded text-[10px] font-mono font-black uppercase tracking-wider cursor-pointer ${
                      isPilotRequested 
                        ? 'bg-purple-600/20 text-purple-400 border border-purple-500/30' 
                        : 'bg-gradient-to-r from-emerald-500 to-teal-500 text-black hover:opacity-90'
                    }`}
                  >
                    {isPilotRequested ? '✓ pilot request active' : '🚀 Request Immediate 14-day Pilot'}
                  </button>
                </div>
              )}

              {/* SERVER SYNC ACTION */}
              <div className="pt-2 border-t border-white/5 space-y-1.5">
                {activeRole === 'guest' ? (
                  <div className="text-[9px] font-mono text-zinc-500 text-center py-1 bg-zinc-950/40 rounded border border-white/5">
                    💾 Preview Mode Only — passport updates are mock stored in client memory
                  </div>
                ) : (
                  <button
                    onClick={async () => {
                      setLoading(true);
                      setErrorMsg(null);
                      try {
                        const headers = {
                          'Content-Type': 'application/json',
                        };
                        const payload = translateProfileToBackend(launchpadData);
                        const res = await fetch('/api/direct-delivery/courier-profile', {
                          method: 'PUT',
                          headers,
                          body: JSON.stringify(payload)
                        });
                        if (res.ok) {
                          onAddLog('Security System', 'Saved profile passport configuration securely to SQLite ledger node', 'SUCCESS', 'Verification holds maintained.');
                          addAuditLog('Saved and synchronised Passport profile to permanent database server.');
                          await syncDataFromServer();
                        } else {
                          const errData = await res.json();
                          throw new Error(errData.error || 'Failed to save profile passport');
                        }
                      } catch (err: any) {
                        setErrorMsg(err.message || 'Network exception saving profile');
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={loading}
                    className="w-full py-2 bg-gradient-to-r from-purple-800/40 to-indigo-800/40 hover:from-purple-800/60 hover:to-indigo-800/60 border border-purple-500/40 text-[#00ffff] rounded text-[9.5px] font-mono font-black uppercase tracking-wider cursor-pointer"
                  >
                    {loading ? '⚡ Synchronising...' : '💾 SAVE PASSPORT CONTRACT (SERVER SYNC)'}
                  </button>
                )}
              </div>

              {/* Progress Slider buttons */}
              <div className="flex gap-2 pt-3 border-t border-white/5">
                <button
                  disabled={onboardingStep === 1}
                  onClick={() => setOnboardingStep(prev => prev - 1)}
                  className="px-3 py-1.5 bg-zinc-900 hover:bg-zinc-800 disabled:opacity-40 rounded text-[9.5px] font-mono font-black uppercase tracking-wider border border-white/5 cursor-pointer text-zinc-400 hover:text-white"
                >
                  Prev
                </button>
                <div className="flex-1 bg-zinc-900 rounded-full h-1.5 self-center overflow-hidden border border-white/5">
                  <div 
                    className="bg-gradient-to-r from-purple-500 to-cyan-400 h-full transition-all duration-300"
                    style={{ width: `${onboardingStep * 10}%` }}
                  />
                </div>
                {onboardingStep < 10 ? (
                  <button
                    onClick={() => setOnboardingStep(prev => prev + 1)}
                    className="px-3 py-1.5 bg-[#00ffff]/10 hover:bg-[#00ffff]/20 rounded text-[9.5px] font-mono font-black uppercase tracking-wider border border-[#00ffff]/20 cursor-pointer text-[#00ffff]"
                  >
                    Next
                  </button>
                ) : (
                  <button
                    onClick={() => setOnboardingStep(1)}
                    className="px-2 py-1.5 bg-zinc-900 text-zinc-450 hover:text-white rounded text-[8px] font-mono uppercase font-black cursor-pointer flex items-center gap-1"
                  >
                    <RotateCcw className="w-3 h-3" />
                    <span>Reset</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* MODULE 2: HOLOGRAPHIC COURIER BUSINESS PASSPORT */}
          <div className="bg-gradient-to-b from-[#130d22]/90 to-[#030612]/95 p-5 rounded-3xl border border-[#00ffff]/30 relative overflow-hidden shadow-[0_0_25px_rgba(168,85,247,0.1)]">
            
            {/* Ambient cyan scanning line effect */}
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-[#00ffff]/40 animate-pulse shadow-[0_0_8px_rgba(0,255,255,0.8)]" />
            
            <div className="flex justify-between items-start border-b border-purple-500/20 pb-3">
              <div>
                <span className="text-[7.5px] font-mono font-black uppercase tracking-widest text-amber-500 bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/30 flex items-center gap-1 w-max">
                  <Sparkles className="w-2.5 h-2.5 text-amber-400" />
                  <span>VERIFIED STATUS: TIER 3 GOLD</span>
                </span>
                <h3 className="text-xs font-black uppercase block tracking-widest text-white font-mono mt-1.5">
                  Courier Business Passport
                </h3>
              </div>
              <div className="text-right text-[7.5px] text-zinc-500 font-mono">
                PASSPORT ID:<br/>
                <span className="text-[#00ffff] font-bold">NND-PASS-951</span>
              </div>
            </div>

            {/* Passport Body */}
            <div className="space-y-3 pt-3 text-left">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 bg-gradient-to-br from-[#122e3b] to-[#3a154c] rounded-xl border border-purple-500/30 flex items-center justify-center text-xl shrink-0 relative shadow-[inset_0_0_12px_rgba(0,255,255,0.2)]">
                  🧔
                  <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center border-2 border-black text-[8px] font-black text-black">
                    ✓
                  </div>
                </div>
                
                <div className="space-y-0.5 font-sans">
                  <span className="text-xs font-bold text-white uppercase block leading-tight">{launchpadData.name}</span>
                  <span className="text-[9.5px] text-purple-300 font-mono block leading-tight">{launchpadData.tradingName}</span>
                  <div className="flex gap-1.5 mt-1">
                    <span className="text-[8px] font-mono px-1.5 bg-black rounded border border-white/5 uppercase text-zinc-400">
                      Exp: {launchpadData.experienceYears} Years
                    </span>
                    <span className="text-[8px] font-mono px-1.5 bg-purple-500/10 text-purple-300 rounded border border-purple-500/20 uppercase" title={authoriseVehiclePlateReveal ? "Authorized Registration plate" : "Disclosure not authorized by courier"}>
                      Plate: {authoriseVehiclePlateReveal ? launchpadData.vehiclePlate : `${launchpadData.vehiclePlate?.substring(0,2)}** *** [REDACTED/HIDDEN]`}
                    </span>
                  </div>
                </div>
              </div>

              {/* Passport Specs List */}
              <div className="bg-black/40 p-3 rounded-xl border border-white/5 space-y-2 text-[9px] font-mono text-zinc-400">
                <div className="flex justify-between border-b border-white/5 pb-1 uppercase">
                  <span>Operating Envelope:</span>
                  <span className="text-white font-bold">{launchpadData.postcodes?.join(', ')}</span>
                </div>
                <div className="flex justify-between border-b border-white/5 pb-1 uppercase">
                  <span>Capacity Vehicle:</span>
                  <span className="text-[#00ffff] font-black">{launchpadData.vehicleType?.toUpperCase().replace('_', ' ')}</span>
                </div>
                <div className="flex justify-between border-b border-white/5 pb-1 uppercase">
                  <span>Equipment Verified:</span>
                  <span className="text-emerald-400 font-bold">{launchpadData.hasHotBag ? '100% THERMAL FOOD HYGIENE' : 'Standard bag'}</span>
                </div>
                <div className="flex justify-between border-b border-white/5 pb-1 uppercase">
                  <span>Commercial Insurance Status:</span>
                  <span className="text-emerald-400 font-bold flex items-center gap-0.5">
                    <ShieldCheck className="w-3 h-3" /> ACTIVE UNTIL FEB 2027
                  </span>
                </div>
                <div className="flex justify-between border-b border-white/5 pb-1 uppercase">
                  <span>Licence & Regulatory Checked:</span>
                  <span className="text-zinc-300">Self-Declared Documented</span>
                </div>
                <div className="flex justify-between uppercase">
                  <span>Backup Cover Plan:</span>
                  <span className="text-emerald-400 font-bold">100% Emergency Active</span>
                </div>
              </div>

              {/* Verified Badge Disclaimer required by guidelines */}
              <div className="text-[8px] text-zinc-500 border border-white/5 bg-black/50 p-2.5 rounded-lg leading-normal uppercase">
                ⚠️ Platform Disclaimer: NEXORAX provides robust verification management tools. Independent logistics platform histories are self-narrated screenshots unless verified via automated document ledger verification in Evidence Upload Preview (Preview only).
              </div>
            </div>
          </div>

        </div>

        {/* ===================== CENTRAL ZONE: DETAILS AND ACTIONS (5 cols) ===================== */}
        <div className="xl:col-span-5 space-y-6">
          <EvidenceUpload 
            activeRole={activeRole} 
            tenantId={tenantId} 
            onAddLog={onAddLog} 
          />

          {/* MODULE 4: FLEET CONSTELLATION COURIER GROUP */}
          <div className="bg-zinc-950/85 p-5 rounded-3xl border border-purple-500/10 space-y-4">
            <div className="flex justify-between items-center border-b border-white/5 pb-2">
              <h3 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-1.5">
                <Users className="w-4 h-4 text-purple-400" />
                <span>Fleet Constellation</span>
              </h3>
              <span className="text-[8.5px] font-mono text-emerald-400 tracking-wider">
                NO ROADS LEFT UNMANNED
              </span>
            </div>

            {/* Invited driver link UI */}
            <form onSubmit={sendInviteFriend} className="flex gap-1.5 font-sans">
              <input
                type="text"
                placeholder="Invite local driver (phone / email)"
                value={inviteFriendName}
                onChange={(e) => setInviteFriendName(e.target.value)}
                className="flex-1 bg-black/60 border border-white/10 rounded px-2.5 py-1 text-[10.5px] text-white outline-none focus:border-purple-500 font-mono"
              />
              <select
                value={inviteFriendRole}
                onChange={(e) => setInviteFriendRole(e.target.value as any)}
                className="bg-zinc-900 text-purple-400 text-[9px] px-1 font-mono uppercase font-black rounded border border-white/5 outline-none"
              >
                <option value="Backup Driver">Backup Driver</option>
                <option value="Emergency Cover">Emergency Cover</option>
              </select>
              <button
                type="submit"
                disabled={activeRole === 'guest'}
                className="px-2.5 py-1 bg-[#00ffff] hover:opacity-90 text-black font-black text-[10px] rounded uppercase cursor-pointer"
              >
                Invite (Preview only)
              </button>
            </form>

            {/* active team list */}
            <div className="space-y-1.5 text-left font-sans">
              {teamMembers.map((m) => (
                <div key={m.id} className="p-2.5 bg-black/40 rounded-xl border border-white/5 flex justify-between items-center text-[10px]">
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-white uppercase">{m.name}</span>
                      <span className={`text-[7.5px] font-mono px-1.5 font-black uppercase rounded ${
                        m.role === 'Primary Driver' ? 'bg-[#00ffff]/10 text-[#00ffff] border border-[#00ffff]/20' : 'bg-purple-900/20 text-purple-400 border border-purple-500/10'
                      }`}>
                        {m.role}
                      </span>
                    </div>
                    <p className="text-[8.5px] text-zinc-500 uppercase font-mono">
                      Ride: {authoriseVehiclePlateReveal ? m.vehicle : m.vehicle.replace(/Plate:\s*([A-Za-z0-9\s]+)/i, 'Plate: [REDACTED/HIDDEN]')} • Schedule: {m.availability}
                    </p>
                  </div>
                  <div className="text-right flex flex-col items-end gap-1 font-mono">
                    <span className="text-[8.5px] text-zinc-400">{m.coverageZone}</span>
                    <button 
                      onClick={async () => {
                        if (activeRole === 'guest') {
                          setTeamMembers(prev => prev.filter(t => t.id !== m.id));
                          addAuditLog(`Suspended/Removed team driver member: ${m.name}`);
                          return;
                        }

                        setLoading(true);
                        try {
                          const headers = {};

                          // Fetch teams to find teamId
                          const teamsRes = await fetch('/api/direct-delivery/teams', { headers });
                          if (teamsRes.ok) {
                            const { data } = await teamsRes.json();
                            const firstTeam = data[0];
                            if (firstTeam) {
                              const res = await fetch(`/api/direct-delivery/teams/${firstTeam.id}/members/${m.id}`, {
                                method: 'DELETE',
                                headers
                              });
                              if (res.ok) {
                                onAddLog('Fleet Constellation', 'Removed driver member securely from SQLite', 'SUCCESS', 'Database row purged.');
                                addAuditLog(`Removed real fleet SQL database member: ${m.name}`);
                                await syncDataFromServer();
                              } else {
                                const errData = await res.json();
                                throw new Error(errData.error || 'Failed to delete member row');
                              }
                            }
                          }
                        } catch (err: any) {
                          setErrorMsg(err.message || 'Error deleting team member from server.');
                        } finally {
                          setLoading(false);
                        }
                      }}
                      disabled={activeRole !== 'team_owner' && activeRole !== 'admin'}
                      className="text-rose-400 hover:text-rose-300 font-black text-[8px] uppercase disabled:opacity-30 cursor-pointer"
                    >
                      Suspend
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Coverage gap warning block */}
            <div className="p-3 bg-purple-500/5 rounded-2xl border border-purple-500/20 text-[9px] text-left font-mono text-purple-300 space-y-1">
              <span className="text-purple-400 font-black block uppercase tracking-wider text-[9.5px]">
                🛰️ CONSTELLATION SYSTEM COVERAGE DETECTOR
              </span>
              <p className="text-zinc-400 leading-snug">
                Coverage simulation: Peak late-night weekend cover (Fri & Sat 11pm - 3am) matches Eleanor Central cover availability under localized simulator parameters.
              </p>
              <div className="flex justify-between items-center text-[8.5px] border-t border-purple-500/10 pt-1.5 mt-1 text-[#00ffff]">
                <span>BACKUP COVERAGE PARAMETERS:</span>
                <span>MATCHED (FRONTEND ONLY)</span>
              </div>
            </div>
          </div>

        </div>

        {/* ===================== RIGHT SIDEBAR: PROPOSALS & STATS (5 cols) ===================== */}
        <div className="xl:col-span-3 space-y-6">
          
          {/* MODULE 5: MERCHANT SAVINGS STUDIO COMPARISON CALCULATOR */}
          <div className="bg-gradient-to-b from-[#160d2b] to-[#020510] p-5 rounded-3xl border border-purple-500/30 space-y-4">
            <h3 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-1.5 border-b border-purple-500/25 pb-2">
              <TrendingUp className="w-4 h-4 text-purple-400" />
              <span>Merchant Savings Studio</span>
            </h3>

            {/* Merchant inputs */}
            <div className="space-y-3.5 text-left font-sans text-[10.5px]">
              <div className="bg-black/50 p-3 rounded-xl border border-white/5 space-y-2">
                <span className="text-[#00ffff] font-black text-[9px] uppercase tracking-wider font-mono block">
                  🏢 MERCHANT SITE DISCOVERY VARIABLES
                </span>
                
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Weekly Deliveries:</span>
                    <input
                      type="number"
                      value={merchantVolume}
                      onChange={(e) => setMerchantVolume(Math.max(1, parseInt(e.target.value) || 0))}
                      className="w-16 bg-zinc-900 border border-white/10 rounded font-mono font-bold text-center text-white"
                    />
                  </div>

                  <div className="flex justify-between">
                    <span className="text-zinc-400">Average distance (mi):</span>
                    <input
                      type="number"
                      step="0.1"
                      value={avgDistance}
                      onChange={(e) => setAvgDistance(parseFloat(e.target.value) || 0)}
                      className="w-16 bg-zinc-900 border border-white/10 rounded font-mono font-bold text-center text-white"
                    />
                  </div>

                  <div className="flex justify-between">
                    <span className="text-zinc-400">Aggregator fee/job (£):</span>
                    <input
                      type="number"
                      step="0.10"
                      value={currentProviderCost}
                      onChange={(e) => setCurrentProviderCost(parseFloat(e.target.value) || 0)}
                      className="w-16 bg-zinc-900 border border-white/10 rounded font-mono font-bold text-center text-white"
                    />
                  </div>
                </div>
              </div>

              {/* SAVINGS ORBIT CHART OUTCOME (THE SAVINGS ORBIT CHARRT) */}
              <div className="bg-purple-950/30 p-4 rounded-xl border border-purple-500/30 space-y-3 text-center relative overflow-hidden shadow-[0_0_20px_rgba(168,85,247,0.15)] select-none">
                <span className="text-purple-400 font-extrabold block text-[9.5px] uppercase tracking-widest font-mono">
                  🪐 Weekly Savings Orbit
                </span>
                
                {/* SVG Visualizer representing concentric orbits */}
                <div className="w-full h-24 flex items-center justify-center relative">
                  <svg className="w-20 h-20" viewBox="0 0 100 100">
                    {/* Aggregator Cost path */}
                    <circle cx="50" cy="50" r="40" fill="none" stroke="#f43f5e" strokeWidth="1" strokeDasharray="4,4" className="animate-spin duration-1000" />
                    {/* NND cost path */}
                    <circle cx="50" cy="50" r="28" fill="none" stroke="#00ffff" strokeWidth="2" />
                    {/* Glowing core center */}
                    <circle cx="50" cy="50" r="5" fill="#a855f7" className="animate-pulse" />
                  </svg>
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
                    <span className="text-emerald-400 font-black text-xs font-mono block">
                      £{weeklyCalculations.savedVal}
                    </span>
                    <span className="text-[7.5px] text-zinc-500 block uppercase font-mono leading-none">SAVED / WK</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 text-[9px] font-mono border-t border-white/5 pt-2 text-left">
                  <div>
                    <span className="text-zinc-500 block uppercase">CURRENT TAXI COST:</span>
                    <span className="text-rose-400 font-bold">£{weeklyCalculations.nowWeeklyVal}/wk</span>
                  </div>
                  <div>
                    <span className="text-zinc-500 block uppercase">NND CONTRACT FEE:</span>
                    <span className="text-emerald-400 font-bold">£{weeklyCalculations.propWeeklyVal}/wk</span>
                  </div>
                </div>
              </div>

              {/* Disclaimer required by guidelines */}
              <div className="text-[8px] text-zinc-500 leading-normal uppercase">
                Estimated figures based on information supplied by the merchant and courier. Actual results may vary. NEXORAX never inserts rigid simulated guarantee declarations.
              </div>
            </div>
          </div>

          {/* MODULE 6: MERCHANT PROPOSAL GENERATOR & MASTER COURIER APPROVAL */}
          <div className="bg-zinc-950/85 p-5 rounded-3xl border border-purple-500/10 space-y-4">
            <h3 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-1.5 border-b border-white/5 pb-2">
              <FileText className="w-4 h-4 text-purple-400" />
              <span>Merchant Proposal Hub</span>
            </h3>

            <div className="text-left space-y-3 font-sans text-[10.5px]">
              <div className="p-2 bg-black/60 rounded-xl border border-white/5 space-y-1 font-mono">
                <div className="flex justify-between uppercase text-[8px]">
                  <span>Contract Version:</span>
                  <span className="text-white">v1.07 Draft (Controlled Preview)</span>
                </div>
                <div className="flex justify-between uppercase text-[8px]">
                  <span>Proposal Phase Status:</span>
                  <span className="text-[#00ffff] font-bold">{proposalStatus.toUpperCase()}</span>
                </div>
              </div>

              {/* COURIER MANUAL APPROVAL COMPLIANCE CHECKBOX LINK */}
              <div className="bg-purple-950/20 p-3 rounded-lg border border-purple-500/25 space-y-2 font-mono text-[9px]">
                <span className="text-purple-300 font-bold block uppercase text-[9.5px]">
                  🔒 Optional Courier Acknowledgement
                </span>
                <p className="text-zinc-400 leading-snug">
                  In this controlled preview, regional proposals can be acknowledged by the operator. Simulated emails are strictly disabled.
                </p>
                <label className="flex items-center gap-2 cursor-pointer pt-1 text-white select-none">
                  <input
                    type="checkbox"
                    checked={isCourierApproved}
                    onChange={async (e) => {
                      const next = e.target.checked;
                      setIsCourierApproved(next);
                      setProposalStatus(next ? 'Approved by courier' : 'Draft');

                      if (activeRole === 'guest') {
                        onAddLog('Proposal Hub', next ? 'Manual Authorization signed by Courier' : 'Withdrew Courier Signature', 'SUCCESS', 'Proposal manifest updated.');
                        addAuditLog(`Courier state authorize lock: ${next.toString().toUpperCase()}`);
                        return;
                      }

                      setLoading(true);
                      try {
                        const headers = {
                          'Content-Type': 'application/json',
                        };

                        // Ensure proposal exists
                        const getRes = await fetch('/api/direct-delivery/proposals', { headers });
                        let proposalId = '';
                        if (getRes.ok) {
                          const { data } = await getRes.json();
                          if (data && data.length > 0) {
                            proposalId = data[0].id;
                          }
                        }

                        if (!proposalId) {
                          const createRes = await fetch('/api/direct-delivery/proposals', {
                            method: 'POST',
                            headers,
                            body: JSON.stringify({
                              title: 'Standard City Grid Courier Partnership Proposal',
                              proposal_json: JSON.stringify({
                                base_rate: baseDeliveryRate,
                                included_distance: includedDistance,
                                per_mile: perMileRate,
                                notes: 'Created via NEXORAX direct delivery engine.'
                              })
                            })
                          });
                          if (createRes.ok) {
                            const { data } = await createRes.json();
                            proposalId = data.id;
                          }
                        }

                        if (proposalId) {
                          if (next) {
                            // Approve
                            const approveRes = await fetch(`/api/direct-delivery/proposals/${proposalId}/approve`, {
                              method: 'POST',
                              headers
                            });
                            if (approveRes.ok) {
                              onAddLog('Proposal Hub', 'Proposal successfully approved and signed in SQLite', 'SUCCESS', 'Database lock active.');
                              addAuditLog(`Sustained permanent courier signature inside SQLite on proposal: ${proposalId}`);
                            }
                          } else {
                            // Draft and clean signature
                            const revertRes = await fetch(`/api/direct-delivery/proposals/${proposalId}`, {
                              method: 'PUT',
                              headers,
                              body: JSON.stringify({
                                title: 'Standard City Grid Courier Partnership Proposal',
                                proposal_json: JSON.stringify({
                                  base_rate: baseDeliveryRate,
                                  included_distance: includedDistance,
                                  per_mile: perMileRate,
                                  notes: 'Reverted to draft via NEXORAX direct delivery engine.'
                                })
                              })
                            });
                            if (revertRes.ok) {
                              onAddLog('Proposal Hub', 'Reverted proposal to draft in database node', 'SUCCESS', 'Signature withdrawn.');
                              addAuditLog(`Signature withdrawn on proposal: ${proposalId}`);
                            }
                          }
                          await syncDataFromServer();
                        }
                      } catch (err: any) {
                        setErrorMsg(err.message || 'Error syncing proposal status to server.');
                      } finally {
                        setLoading(false);
                      }
                    }}
                    className="accent-emerald-500 w-4 h-4"
                  />
                  <span>Sustain Complete Courier Approval</span>
                </label>
              </div>

              <div className="bg-black/50 p-3 rounded-xl border border-white/5 space-y-1.5 text-[8.5px] uppercase font-mono">
                <span className="text-zinc-400 block font-bold">Proposed SLA PLEDGE Targets:</span>
                <div className="flex justify-between">
                  <span>🚀 Max Delivery Turnaround:</span>
                  <span className="text-white">22 mins</span>
                </div>
                <div className="flex justify-between">
                  <span>⏱️ Average Pickup waiting allowance:</span>
                  <span className="text-white">8 mins</span>
                </div>
                <div className="flex justify-between">
                  <span>📦 Intact Food proof pin policy:</span>
                  <span className="text-white">Enabled</span>
                </div>
              </div>
            </div>
          </div>

        </div>

      </div>

      {/* ===================== DOWN SECTION: LOWER SECTOR MATRIX (12 cols) ===================== */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 relative z-10 font-mono">
        
        {/* MODULE 7: MERCHANT OPPORTUNITY CRM (6 cols) */}
        <div className="lg:col-span-6 bg-zinc-950/85 p-5 rounded-3xl border border-purple-500/10 space-y-4">
          <div className="flex justify-between items-center border-b border-zinc-900 pb-2">
            <h3 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-1.5">
              <Sparkles className="w-4 h-4 text-purple-400" />
              <span>Merchant Opportunity CRM Pipeline</span>
            </h3>
            <span className="text-[8px] bg-purple-500/15 text-purple-300 px-2 py-0.5 rounded border border-purple-500/25">
              PROSPECTING ACTIVE
            </span>
          </div>

          <form onSubmit={saveOpportunity} className="grid grid-cols-1 md:grid-cols-3 gap-2.5 text-left text-[10px]">
            <div className="space-y-1">
              <label className="text-[8px] text-zinc-500 uppercase">Merchant Legal Brand *</label>
              <input
                type="text"
                placeholder="e.g. Royal Pharmacy"
                value={newOppName}
                onChange={(e) => setNewOppName(e.target.value)}
                className="w-full bg-black border border-white/10 rounded px-2 py-1 text-white text-[10px]"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[8px] text-zinc-500 uppercase">Primary Contact Person</label>
              <input
                type="text"
                placeholder="e.g. Marcello Russo"
                value={newOppContact}
                onChange={(e) => setNewOppContact(e.target.value)}
                className="w-full bg-black border border-white/10 rounded px-2 py-1 text-white text-[10px]"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[8px] text-zinc-500 uppercase">Current Method</label>
              <select
                value={newOppMethod}
                onChange={(e: any) => setNewOppMethod(e.target.value)}
                className="w-full bg-black border border-white/10 rounded px-1.5 py-1 text-[#00ffff] font-black text-[9.5px]"
              >
                <option value="Uber Eats">Uber Eats (30% Levy)</option>
                <option value="Just Eat">Just Eat (JET Platform)</option>
                <option value="Deliveroo">Deliveroo Signature</option>
                <option value="Own Drivers">Local In-House Team</option>
                <option value="No delivery">No Delivery Service</option>
              </select>
            </div>
            <div className="space-y-1 md:col-span-2">
              <label className="text-[8px] text-zinc-500 uppercase">Branch Address</label>
              <input
                type="text"
                placeholder="Full address details"
                value={newOppAddress}
                onChange={(e) => setNewOppAddress(e.target.value)}
                className="w-full bg-black border border-white/10 rounded px-2 py-1 text-white text-[10px]"
              />
            </div>
            <div className="space-y-1 self-end">
              <button
                type="submit"
                disabled={activeRole === 'guest'}
                className="w-full py-1.5 bg-purple-600/30 hover:bg-purple-600/50 border border-purple-500/20 text-[#00ffff] font-black text-[9px] uppercase rounded cursor-pointer"
              >
                Add Prospect (Preview only)
              </button>
            </div>
          </form>

          {/* CRM Leads Table List */}
          <div className="space-y-2 font-sans text-left">
            {opps.map((o) => (
              <div key={o.id} className="p-3 bg-black/40 rounded-xl border border-white/5 space-y-2">
                <div className="flex justify-between items-start">
                  <div>
                    <span className="text-white text-[11px] font-bold block uppercase">{o.name}</span>
                    <span className="text-[8px] text-zinc-500 font-mono block uppercase">
                      Class: {o.type} • Address: {o.location} • Source: {o.source}
                    </span>
                  </div>
                  <span className={`text-[7.5px] font-mono uppercase bg-purple-500/10 text-[#00ffff] border border-cyan-500/20 font-black px-1.5 rounded`}>
                    {o.stage.replace('_', ' ')}
                  </span>
                </div>

                <div className="flex flex-wrap justify-between items-center text-[9px] font-mono border-t border-white/5 pt-2 gap-1.5">
                  <div className="text-zinc-400">
                    Authority: <strong className="text-zinc-200">{o.decisionAuthority} ({o.contactPerson})</strong>
                  </div>
                  
                  {/* Phase changes switcher */}
                  <div className="flex gap-1">
                    {(['Researching', 'Contacted', 'Pilot active', 'Won'] as const).map((st) => (
                      <button
                        key={st}
                        onClick={() => updateOppStage(o.id, st)}
                        disabled={activeRole === 'guest'}
                        className={`px-1.5 py-0.5 rounded text-[8px] font-bold cursor-pointer transition-colors ${
                          o.stage === st 
                            ? 'bg-[#00ffff]/20 text-[#00ffff] border border-[#00ffff]/30' 
                            : 'bg-zinc-900 text-zinc-500 hover:text-white border border-transparent'
                        }`}
                      >
                        {st}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* MODULE 8: PILOT PROGRAMME BUILDER MATRIX (6 cols) */}
        <div className="lg:col-span-6 bg-zinc-950/85 p-5 rounded-3xl border border-purple-500/10 space-y-4">
          <div className="flex justify-between items-center border-b border-zinc-900 pb-2">
            <h3 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-1.5">
              <Zap className="w-4 h-4 text-[#00ffff]" />
              <span>Pilot Programme Builder & Target Scorecard</span>
            </h3>
            <span className="text-[7.5px] font-mono text-emerald-400 font-black bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded uppercase">
              COURIER DIRECT REVIEW LOCKED
            </span>
          </div>

          <div className="space-y-4 text-left">
            {pilots.map((p) => (
              <div key={p.id} className="p-4 bg-[#030612] rounded-2xl border border-purple-500/20 space-y-3 font-sans">
                <div className="flex justify-between items-center">
                  <div>
                    <span className="text-white text-xs font-bold block uppercase">{p.merchantName}</span>
                    <span className="text-[9px] text-zinc-500 font-mono uppercase block">{p.operatingHours} • Site: {p.branchLocation}</span>
                  </div>
                  <span className={`text-[8px] font-mono font-black uppercase px-2 py-1 rounded ${
                    p.humanApproval === 'Approved' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-amber-500/10 text-amber-500'
                  }`}>
                    Human Check: {p.humanApproval}
                  </span>
                </div>

                {/* Scorecard grids (Target Performance Measures - Module 8) */}
                <span className="text-[8px] text-purple-300 font-mono font-black tracking-widest block uppercase border-b border-white/5 pb-1">
                  📊 PILOT PERFORMANCE SCORECARD
                </span>
                
                <div className="grid grid-cols-3 md:grid-cols-4 gap-2.5 text-center font-mono">
                  <div className="p-1 px-1.5 bg-black/40 border border-white/3 rounded">
                    <span className="text-zinc-500 block text-[7.5px] uppercase truncate">Jobs Offered</span>
                    <span className="text-white text-[11px] font-black">{p.performance.jobsOffered}</span>
                  </div>
                  <div className="p-1 px-1.5 bg-black/40 border border-white/3 rounded">
                    <span className="text-zinc-500 block text-[7.5px] uppercase truncate">Accepted %</span>
                    <span className="text-[#00ffff] text-[11px] font-black">{p.performance.jobsAccepted}%</span>
                  </div>
                  <div className="p-1 px-1.5 bg-black/40 border border-white/3 rounded">
                    <span className="text-zinc-500 block text-[7.5px] uppercase truncate">Pickup Punct</span>
                    <span className="text-emerald-400 text-[11px] font-black">{p.performance.pickupPunctuality}%</span>
                  </div>
                  <div className="p-1 px-1.5 bg-black/40 border border-white/3 rounded">
                    <span className="text-zinc-500 block text-[7.5px] uppercase truncate">Avg Duration</span>
                    <span className="text-white text-[11px] font-black">{p.performance.avgDeliveryMinutes}m</span>
                  </div>
                  <div className="p-1 px-1.5 bg-black/40 border border-white/3 rounded">
                    <span className="text-zinc-500 block text-[7.5px] uppercase truncate">Pod Proof %</span>
                    <span className="text-emerald-400 text-[11px] font-black">{p.performance.proofOfDeliveryRate}%</span>
                  </div>
                  <div className="p-1 px-1.5 bg-black/40 border border-white/3 rounded">
                    <span className="text-zinc-500 block text-[7.5px] uppercase truncate">Fails Rec</span>
                    <span className="text-rose-400 text-[11px] font-black">{p.performance.failedDeliveries}</span>
                  </div>
                  <div className="p-1 px-1.5 bg-black/40 border border-white/3 rounded">
                    <span className="text-zinc-500 block text-[7.5px] uppercase truncate">Backup Activ</span>
                    <span className="text-[#00ffff] text-[11px] font-black">{p.performance.backupActivations}</span>
                  </div>
                  <div className="p-1 px-1.5 bg-black/40 border border-white/3 rounded col-span-3 md:col-span-1">
                    <span className="text-zinc-500 block text-[7.5px] uppercase truncate">Satisfaction</span>
                    <span className="text-amber-400 text-[11px] font-black">⭐ {p.performance.merchantRating}</span>
                  </div>
                </div>

                <div className="text-[8.5px] text-zinc-500 font-mono uppercase bg-black/55 p-2 rounded-lg border border-white/5 space-y-0.5">
                  <div className="flex justify-between">
                    <span>Target Incident Response Escalation:</span>
                    <span className="text-white">Active (Under 10 mins Cover Rule #12)</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Pilot Agreement terms:</span>
                    <span className="text-zinc-400">14 Days No-Obligation, Human Audited</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* ===================== MODULE 9: LIVE SECTOR ENVELOPE MISSION CONTROL ===================== */}
      <div className="bg-zinc-950/85 p-5 rounded-3xl border border-cyan-500/10 space-y-4 text-left">
        <div className="flex justify-between items-center border-b border-zinc-900 pb-2">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-500 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-400"></span>
            </span>
            <span className="text-xs font-black text-white uppercase tracking-wider">
              NEXORAX Live Mission Control Dashboard
            </span>
          </div>
          <span className="text-[8.5px] text-[#00ffff] font-mono uppercase tracking-widest bg-cyan-950/30 px-2 py-0.5 rounded border border-[#00ffff]/20">
            PREVIEW DEMONSTRATION ACTIVE
          </span>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">

          {/* Galaxy Interactive Map Visualizer (4 cols) */}
          <div className="xl:col-span-4 bg-black/60 p-4 rounded-2xl border border-purple-500/10 h-72 relative overflow-hidden flex flex-col justify-between">
            <span className="text-[8.5px] text-purple-300 font-bold uppercase tracking-widest block font-mono z-10">
              🌌 Simulated Dispatch Constellation Location
            </span>

            {/* Glowing Star Grid background representing delivery path */}
            <div className="absolute inset-0 z-0 bg-[radial-gradient(#1e1b4b_1px,transparent_1px)] [background-size:16px_16px] opacity-45" />

            <div className="relative w-full h-full z-10">
              {/* Outer Ring boundary */}
              <div className="absolute inset-4 border border-dashed border-white/5 rounded-full" />
              <div className="absolute inset-14 border border-dashed border-[#00ffff]/5 rounded-full" />
              
              {/* Central Kitchen node */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center">
                <div className="w-5 h-5 rounded-full bg-[#00ffff] flex items-center justify-center border-2 border-black animate-pulse shadow-[0_0_12px_rgba(0,255,255,0.7)]">
                  🏢
                </div>
                <span className="text-[7.5px] text-white font-black uppercase tracking-wider block mt-1 bg-black/75 px-1.5 py-0.5 rounded border border-white/10 font-mono">
                  KIT NODE-NX
                </span>
              </div>

              {/* Dynamic Job location points */}
              {liveJobs.map((j) => (
                <div
                  key={j.id} 
                  className="absolute"
                  style={{ top: `${j.coordinates.y}%`, left: `${j.coordinates.x}%` }}
                >
                  <div className={`p-1 bg-zinc-950/80 rounded-lg border border-purple-500/30 text-[8px] font-mono flex items-center gap-1 leading-none shadow-[0_4px_12px_rgba(0,0,0,0.5)]`}>
                    <span className="w-2 h-2 rounded-full bg-purple-500 animate-ping block" style={{ animationDuration: '3s' }} />
                    <span className="text-white block font-bold">{j.id}: {j.driverName.split(' ')[0]} ({j.eta}m)</span>
                  </div>
                  {/* Animated path line back to kitchen */}
                  <svg className="absolute -top-10 -left-10 w-24 h-24 pointer-events-none z-0 overflow-visible opacity-30">
                    <line x1="40" y1="40" x2="10" y2="10" stroke="#a855f7" strokeWidth="1" strokeDasharray="2,2" />
                  </svg>
                </div>
              ))}
            </div>

            <div className="text-[7.5px] text-zinc-500 leading-normal font-sans text-left z-10 font-mono">
              🚧 GRAPHIC DISPATCH STATUS: Real-time map coordinate simulations are active for preview mode only.
            </div>
          </div>

          {/* REAL PERSISTENT DIRECT DELIVERY COMPLIANCE CONTROL & DRIVER PANEL (8 cols) */}
          <div className="xl:col-span-8 flex flex-col justify-between">
            <ActiveDeliveryPanel
              activeRole={activeRole}
              tenantId={tenantId}
              onAddLog={onAddLog}
              onTriggerProofOfDelivery={(deliveryId) => {
                setActivePodDeliveryId(deliveryId);
              }}
              onTriggerTrackingView={handleTriggerTrackingView}
            />
          </div>

        </div>
      </div>

      {/* ===================== MODULE 11: FINANCIALS, TRANSPARENCY & DATABASE VIEWER ===================== */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 relative z-10 text-left">
        
        {/* RATES & TRANS TRANSPARENT CARD (6 cols) */}
        <div className="lg:col-span-6 bg-[#030612]/90 p-5 rounded-3xl border border-white/5 space-y-4">
          <div className="flex justify-between items-center border-b border-zinc-900 pb-2">
            <h3 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-1.5">
              <Coins className="w-4 h-4 text-amber-500" />
              <span>Transparent Rates & Invoices (Module 11)</span>
            </h3>
            <span className="text-[8px] bg-amber-500/10 text-amber-500 border border-amber-500/25 px-2 py-0.5 rounded font-black font-mono">
              NO PERCENTAGE COMMISSION HOOK
            </span>
          </div>

          <div className="space-y-3 font-sans text-[10px]">
            <p className="text-zinc-400 leading-relaxed font-mono uppercase text-[9px]">
              NEXORAX operates with extreme platform fee transparency. Our flat platform service fee is capped at exactly <strong className="text-white font-mono">£{platformFeeConfig.fee_amount} flat per delivery job</strong>. We never insert hidden service markups or percentage commissions.
            </p>

            <div className="space-y-1.5 font-mono text-[9px] uppercase">
              <span className="text-zinc-500 block">Weekly Payout Settlements:</span>
              <div className="p-3 bg-rose-500/5 rounded-2xl border border-rose-500/10 text-rose-300 font-bold font-mono text-center">
                Payment processing is unavailable in this controlled preview.
              </div>
            </div>

            <div className="p-3 bg-purple-500/5 rounded-2xl border border-purple-500/10 font-mono text-[8.5px] uppercase text-purple-300">
              ⚡ COMPLIANCE NOTE: Tax/VAT is self-declared by independent couriers unless the region executes local billing agency record overrides matching NI mandates.
            </div>
          </div>
        </div>

        {/* DATABASE SCHEMA & SECURITY CONTROLS COMPONENT (6 cols) */}
        <div className="lg:col-span-6 bg-zinc-950/85 p-5 rounded-3xl border border-[#00ffff]/20 space-y-4 font-mono text-left">
          <div className="flex justify-between items-center border-b border-zinc-900 pb-2">
            <h3 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-1.5">
              <FileCode className="w-4 h-4 text-[#00ffff]" />
              <span>Module 15: Schema & Integration Blocker Diagnostic</span>
            </h3>
            <span className="text-[7.5px] text-amber-500 border border-amber-500/25 px-2 py-0.5 rounded uppercase font-bold">
              Frontend Preview
            </span>
          </div>

          <div className="text-[10px] text-zinc-400 uppercase leading-normal font-sans space-y-2">
            <p>
              Status: <strong className="text-amber-500 font-bold">Frontend Preview Only</strong>. Direct Cloud DB connection and authenticated courier profile sync are disabled for this preview.
            </p>
            <div className="p-2 border border-rose-500/30 bg-rose-500/5 rounded text-rose-300 space-y-1">
              <strong className="text-[8.5px] text-rose-400 block font-mono">⚠️ ACTUAL INTEGRATION BLOCKERS:</strong>
              <ul className="list-disc pl-3.5 space-y-1 text-[8px] font-mono leading-tight">
                <li>No backend APIs/servers linked directly for multi-party verification.</li>
                <li>Firebase Storage and live Firebase Authentication are not configured or initialized.</li>
                <li>Live GPS/navigation calculations are mock and bypassed (Routing APIs inactive).</li>
                <li>Real Payment Processor (Stripe/SPP integrations) are inactive.</li>
                <li>Document malware scanning, append-only auditing, and signed URLs are not implemented.</li>
              </ul>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-center text-[8.5px] font-mono leading-none">
            <div className="p-2 bg-black border border-white/5 rounded">
              <span className="text-[#00ffff] block font-bold mb-1">courier_profiles</span>
              <span className="text-zinc-500 block uppercase font-normal text-[7px]">Tenant Document</span>
            </div>
            <div className="p-2 bg-black border border-white/5 rounded">
              <span className="text-[#00ffff] block font-bold mb-1">courier_teams</span>
              <span className="text-zinc-500 block uppercase font-normal text-[7px]">Object Policy Rules</span>
            </div>
            <div className="p-2 bg-black border border-white/5 rounded">
              <span className="text-[#00ffff] block font-bold mb-1 font-sans">deliveries</span>
              <span className="text-zinc-500 block uppercase font-normal text-[7px]">Secure Document</span>
            </div>
            <div className="p-2 bg-black border border-white/5 rounded">
              <span className="text-[#00ffff] block font-bold mb-1">rate_cards</span>
              <span className="text-zinc-500 block uppercase font-normal text-[7px]">Immutable Ledger</span>
            </div>
            <div className="p-2 bg-black border border-white/5 rounded">
              <span className="text-[#00ffff] block font-bold mb-1">document_vault</span>
              <span className="text-amber-400 block font-bold mb-1 text-[8px]">Secure App Files</span>
            </div>
            <div className="p-2 bg-black border border-white/5 rounded">
              <span className="text-[#00ffff] block font-semibold mb-1">audit_events</span>
              <span className="text-zinc-500 block uppercase font-normal text-[7px]">Simulated activity history</span>
            </div>
          </div>

          {/* SECURITY AUDIT TRAIL LOG LIST */}
          <div className="p-3.5 bg-black border border-white/5 rounded-xl space-y-2">
            <span className="text-[8.5px] text-zinc-300 font-bold block uppercase border-b border-white/5 pb-1">
              🕵️ Simulated activity history — not a server audit record.
            </span>
            <div className="space-y-1.5 max-h-[100px] overflow-y-auto text-[8px] uppercase tracking-normal">
              {auditTrail.map((ev) => (
                <div key={ev.id} className="flex justify-between select-text selection:bg-purple-900 text-zinc-450 leading-tight">
                  <span className="text-purple-300 shrink-0 font-bold font-mono text-[7px] bg-purple-500/10 px-1 py-0.5 rounded leading-none text-center block my-auto">{ev.timestamp}</span>
                  <span className="text-zinc-300 px-2 pl-3 flex-1">{ev.action}</span>
                  <span className="text-cyan-400 text-right font-semibold font-mono">{ev.actor}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>

      {/* FOOTER & BRAND LEGAL HEADLINES (Required by instructions) */}
      <div className="relative z-10 border-t border-purple-500/20 pt-6 mt-6 space-y-4">
        
        {/* Brand Wording Headings */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
          <div className="space-y-1">
            <span className="text-xs font-black uppercase text-white tracking-wider font-mono">
              🏬 MERCHANTS & FRANCHISEE OPERATORS
            </span>
            <p className="text-[10px] text-zinc-400 font-sans leading-relaxed">
              Build a reliable, cost-transparent local delivery fleet with experienced independent couriers and measurable SLA core metrics. Eliminate aggregator dependency fees entirely.
            </p>
          </div>
          <div className="space-y-1">
            <span className="text-xs font-black uppercase text-purple-300 tracking-wider font-mono">
              🚲 INDEPENDENT COURIERS & TRADING FLEETS
            </span>
            <p className="text-[10px] text-zinc-400 font-sans leading-relaxed">
              You already have extensive delivery platform experience. Formulate the undeniable proof, build your fleet constellation standby network, and win high-paying direct merchant contracts.
            </p>
          </div>
        </div>

        {/* Control Preview Disclaimer required by instructions */}
        <div className="p-4 bg-zinc-950/90 rounded-2xl border border-white/5 mt-4 text-[8.5px] text-zinc-500 text-left font-mono uppercase space-y-2 leading-relaxed">
          <div className="flex items-center gap-1.5">
            <Info className="w-4 h-4 text-zinc-500 shrink-0" />
            <span className="font-bold text-zinc-400">DISCLAIMER & REGULATORY REMINDERS</span>
          </div>
          <p>
            NEXORAX provides direct dispatch coordination layout capabilities. Brand names including McDonald's, Burger King, KFC, Domino's, Uber Eats, Just Eat, and Deliveroo are properties of their respective trademark holders; no physical affiliation is implied, nor does NEXORAX claim direct integrations without regional merchant client token configuration. Independent contractors must secure valid commercial Hire & Reward insurance suitable for food carriage or health transport in their geographical region.
          </p>
          <div className="p-2.5 bg-[#00ffff]/5 rounded-lg border border-[#00ffff]/10 text-[#00ffff] font-sans font-bold normal-case text-[10px]">
            NEXORAX Direct Delivery Network interface ready for controlled preview. Live dispatch, document verification, payments, location tracking and provider integrations remain disabled.
          </div>
        </div>

      </div>

    </div>
  );
};
